import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Paper,
  Button,
  Avatar,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  ListItemAvatar,
  Tooltip,
  Alert,
  Badge,
  ListItemButton,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import { LineChart } from "@mui/x-charts";
import {
  CheckBox,
  Close,
  Info,
  Bolt,
  MailOutline,
  CheckCircle,
} from "@mui/icons-material";
import Header from "../components/header";
import ApprovalPendingModal from "../components/ApprovalPendingModal";
import Footer from "../components/footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import getApiBase from "../components/getApiBase";
import { formatUsPhoneDisplay } from "../utils/shopPublicPhone";
import ShopTeamRoster from "../components/ShopTeamRoster";
import ConfidenceScoreSection from "../components/ConfidenceScoreSection";
import ConversationModal from "../components/ConversationModal";
import ServiceRequestCard from "../components/ServiceRequestCard";
import BillingRequiredModal from "../components/BillingRequiredModal";
import PlatformFeeRateChangeModal from "../components/PlatformFeeRateChangeModal";
import { isBillingRequiredError, isRateAckRequiredError, PREVIEW_BILLING_WEBHOOK_NOTICE, isVercelFeatureBranchDeploy } from "../utils/billingSetup";
import { hadBillingSetupReturnOnLoad, parseBillingSetupReturn } from "../utils/billingReturn";
import {
  saveQuoteResumeDraft,
  loadQuoteResumeDraft,
  clearQuoteResumeDraft,
  buildDashboardBillingReturnPath,
  hasQuoteResumePending,
} from "../utils/billingQuoteResume";
import { useBillingSetupReturn } from "../hooks/useBillingSetupReturn";
import { fetchPlatformFeeRateNotice } from "../utils/platformFee";
import {
  availabilitySlotDateOnly,
  endTimeFromStartAndDuration,
  isAvailabilityChipSelected,
  isDropoffAvailabilitySlot,
  normalizeSelectedSlots,
  partitionSelectedAvailability,
  toggleAvailabilityChip,
} from "../utils/directAvailabilityChips";
import { Autocomplete } from "@mui/material";
import {
  format,
  parse,
  parseISO,
  formatDistanceToNow,
  startOfDay,
} from "date-fns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import featuredShieldBadge from "../assets/feature-badges/badge-design-3-shield.png";
import ribbonBadge from "../assets/feature-badges/badge-design-1-ribbon.png";
import sealBadge from "../assets/feature-badges/badge-design-2-seal.png";
import bannerStarBadge from "../assets/feature-badges/badge-design-4-banner-star.png";
import cornerBadge from "../assets/feature-badges/badge-design-5-corner.png";
import reviewsDisplayIcon from "../assets/feature-badges/reviews-display.png";
import extendedHistoryIcon from "../assets/feature-badges/extended-history.png";
import brandingIcon from "../assets/feature-badges/branding.png";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isBefore, isValid } from "date-fns";

// Helper function to get point value for sorting
const getPointValue = (item) => {
  if (item.type === "checkmark" && item.id === "in_network") {
    return 1; // In-network: 1 point
  }

  const name = item.name?.toLowerCase() || "";

  // Priority listing: 10 points
  if (name === "addon_priority_listing" || name.includes("priority")) {
    return 10;
  }

  // Featured badge: 5 points
  if (name === "addon_featured_badge" || name.includes("featured")) {
    return 5;
  }

  // Other subscriptions: 1 point each
  return 1;
};

// Helper function to get badge/image/word art for subscription type
const getSubscriptionDisplay = (subscription) => {
  const name = subscription.name?.toLowerCase() || "";
  const displayName =
    subscription.display_name || subscription.name || "Subscription";

  // Priority listing gets word art instead of badge image
  if (name === "addon_priority_listing" || name.includes("priority")) {
    return {
      type: "wordart",
      label: "Priority",
      tooltip: `${displayName}\n\nAppear at the top of search results for increased visibility.`,
    };
  }
  // Featured badge gets badge image
  if (name === "addon_featured_badge" || name.includes("featured")) {
    return {
      type: "badge",
      image: featuredShieldBadge,
      label: "Featured",
      tooltip: `${displayName}\n\nStand out with a featured badge on your profile and highlighted placement in search results.`,
    };
  }

  // Other subscriptions get badge images
  if (
    name === "addon_service_history" ||
    name.includes("service_history") ||
    name.includes("extended")
  ) {
    return {
      type: "badge",
      image: extendedHistoryIcon,
      label: "History",
      labelColor: "#555",
      tooltip: `${displayName}\n\nAccess extended service records and customer maintenance history.`,
    };
  }
  if (name === "addon_analytics" || name.includes("analytics")) {
    return {
      type: "badge",
      image: bannerStarBadge,
      label: displayName,
      tooltip: `${displayName}\n\nDetailed insights and reporting including performance metrics and customer analytics.`,
    };
  }
  if (name === "addon_branding" || name.includes("branding")) {
    return {
      type: "badge",
      image: brandingIcon,
      label: "Branding",
      tooltip: `${displayName}\n\nCustomize your shop appearance with custom colors, logo, and branded profile page.`,
    };
  }
  if (name === "addon_google_reviews" || name.includes("google_reviews")) {
    return {
      type: "badge",
      image: reviewsDisplayIcon,
      label: "Reviews",
      labelColor: "#D4AF37",
      tooltip: `${displayName}\n\nDisplay your Google reviews on the home page to boost credibility and trust.`,
    };
  }
  if (name === "addon_first_dibs" || name.includes("first_dibs")) {
    return {
      type: "badge",
      image: bannerStarBadge,
      label: displayName,
      tooltip: `${displayName}\n\nGet first access to new service requests before other shops.`,
    };
  }

  // Default fallback
  return {
    type: "badge",
    image: featuredShieldBadge,
    label: displayName,
    tooltip: displayName,
  };
};

const isPastDueAccepted = (req) => {
  const VALID_STATUSES = ["Scheduled", "Accepted", "Confirmed", "In Progress"];

  if (!VALID_STATUSES.includes(req.status)) {
    return false;
  }

  let serviceDay = null;

  if (req.customer_selected_slot_details?.available_date) {
    const [y, m, d] = req.customer_selected_slot_details.available_date
      .split("-")
      .map(Number);

    serviceDay = new Date(y, m - 1, d, 0, 0, 0, 0);
  } else if (req.confirm_date) {
    const [y, m, d] = req.confirm_date.split("-").map(Number);
    serviceDay = new Date(y, m - 1, d, 0, 0, 0, 0);
  } else {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastDue = serviceDay.getTime() < today.getTime();

  // console.log({
  //   id: req.id,
  //   status: req.status,
  //   serviceDay: serviceDay.toDateString(),
  //   today: today.toDateString(),
  //   pastDue,
  // });

  return pastDue;
};

const timeCleanUp = (req) => {
  if (!req) return "N/A";

  try {
    const time = req.toString().split(".")[0];
    const parsed =
      time.length === 5
        ? parse(time, "HH:mm:ss", new Date())
        : parse(time, "HH:mm", new Date());

    return format(parsed, "h:mm a");
  } catch (err) {
    console.error("Error parsing time: ", req.proposed_start_time, err);

    //fallback incase the top code doesn't set it to 12-hour time
    const [hourStr, minuteStr] = req.split(":");
    const hour = parseInt(hourStr);
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? "AM" : "PM";

    return `${hour12}:${minuteStr} ${ampm}`;
  }
};

const formatDateSafe = (req) => {
  if (!req) return "";
  try {
    return format(parseISO(req), "EEE, MMM d, yyyy");
  } catch {
    return req;
  }
};

const dateTimeText = (req) => {
  if (!req) return "N/A";
  console.log("req: ", req);

  if (req.customer_selected_slot_details) {
    const slot = req.customer_selected_slot_details;

    return `Scheduled for: ${formatDateSafe(slot.available_date)} at ${timeCleanUp(slot.start_time)} - ${timeCleanUp(slot.end_time)}`;
  }

  if (req.is_dropoff) {
    return `🚗 Drop-off at ${timeCleanUp(req.proposed_start_time)} (customer picks up at close or when notified)`;
  }

  return `Scheduled for: ${timeCleanUp(req.proposed_start_time)}${req.confirm_date ? ` on ${req.confirm_date}` : ""}`;
};

const getDaysSinceServiceDate = (req) => {
  let serviceDay = null;

  if (req.customer_selected_slot_details?.available_date) {
    const [y, m, d] = req.customer_selected_slot_details.available_date
      .split("-")
      .map(Number);
    serviceDay = new Date(y, m - 1, d, 0, 0, 0, 0);
  } else if (req.confirm_date) {
    const [y, m, d] = req.confirm_date.split("-").map(Number);
    serviceDay = new Date(y, m - 1, d, 0, 0, 0, 0);
  }

  if (!serviceDay) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffMs = today.getTime() - serviceDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays;
};

const canCancelService = (req) => {
  const daysSince = getDaysSinceServiceDate(req);
  return daysSince >= 2;
};

// Review List Item Component with comment preview
const ReviewListItem = ({
  review,
  user,
  onOpenConversation,
  fetchComments,
}) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadComments = async () => {
      // Google reviews don't have service request IDs, so skip loading comments
      if (review.service_request_id && !review.is_google_review) {
        const fetchedComments = await fetchComments(review.service_request_id);
        setComments(fetchedComments);
      }
      setLoading(false);
    };
    loadComments();
  }, [review.service_request_id, review.is_google_review, fetchComments]);

  // Get most recent customer/household comment
  const latestCustomerComment = comments
    .filter(
      (c) => c.author_type === "customer" || c.author_type === "household",
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  return (
    <ListItem
      alignItems="flex-start"
      sx={{ mb: 2, flexDirection: "column", alignItems: "stretch" }}
    >
      <Box display="flex" width="100%">
        <ListItemAvatar>
          <Avatar>
            {(review.first_name?.[0] || "?") + (review.last_name?.[0] || "")}
          </Avatar>
        </ListItemAvatar>
        <Box flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              {review.first_name} {review.last_name}
            </Typography>
            <Rating size="small" value={review.rating} readOnly />
            <Typography variant="caption" color="text.secondary">
              {new Date(review.created_at).toLocaleDateString()}
            </Typography>
          </Stack>

          {/* Review Text / Latest Comment Preview */}
          {review.is_google_review ? (
            // Google reviews show the review text directly
            review.comment ? (
              <Box
                mt={1}
                p={1.5}
                sx={{
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                  borderLeft: "3px solid #4285F4", // Google blue
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Google Review
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {review.comment}
                </Typography>
              </Box>
            ) : null
          ) : // Platform reviews show conversation comments
          loading ? (
            <Typography variant="caption" color="text.secondary">
              Loading comments...
            </Typography>
          ) : latestCustomerComment ? (
            <Box
              mt={1}
              p={1.5}
              sx={{
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                borderLeft: "3px solid #2196f3",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                {latestCustomerComment.author_name}
                {latestCustomerComment.author_type === "household" &&
                  " (Household)"}
                {" • "}
                {new Date(latestCustomerComment.created_at).toLocaleDateString(
                  "en-US",
                  {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  },
                )}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {latestCustomerComment.comment}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              fontStyle="italic"
            >
              No comments yet
            </Typography>
          )}

          {/* Reply Button (Owners Only) - Hidden for Google reviews */}
          {user?.role === "Owner" &&
            !review.is_google_review &&
            review.service_request_id && (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  onOpenConversation(
                    review,
                    review.service_request_id,
                    comments,
                  )
                }
                sx={{ mt: 1, textTransform: "none" }}
              >
                {comments.length > 0 ? "Reply" : "Start Conversation"}
              </Button>
            )}
        </Box>
      </Box>
    </ListItem>
  );
};

// Helper function to convert 12-hour time to 24-hour format
const convertTo24Hour = (hour12, minute, ampm) => {
  let hour24 = parseInt(hour12);
  if (ampm === "PM" && hour24 !== 12) hour24 += 12;
  if (ampm === "AM" && hour24 === 12) hour24 = 0;
  return `${hour24.toString().padStart(2, "0")}:${minute}`;
};

// Helper function to parse 24-hour time to 12-hour components
const parseTo12Hour = (time24) => {
  if (!time24 || time24 === "") return { hour: "9", minute: "00", ampm: "AM" };
  const [hourStr, minuteStr] = time24.split(":");
  if (!hourStr || !minuteStr) return { hour: "9", minute: "00", ampm: "AM" };
  const hour24 = parseInt(hourStr);
  const hour12 =
    hour24 === 0
      ? "12"
      : hour24 > 12
        ? (hour24 - 12).toString()
        : hour24.toString();
  const ampm = hour24 < 12 ? "AM" : "PM";
  return { hour: hour12, minute: minuteStr, ampm };
};

const filterHrsInput = (input) => {
  let value = input.replace(/[^\d.]/g, "");
  const dot = value.indexOf(".");
  if (dot !== -1) {
    value = value.slice(0, dot + 1) + value.slice(dot + 1).replace(/\./g, "");
  }
  const parts = value.split(".");
  if (parts[1] && parts[1].length > 2) {
    value = parts[0] + "." + parts[1].substring(0, 2);
  }
  return value;
};

const normalizeHrsOnBlur = (raw) => {
  if (raw === undefined || raw === null || raw === "") return "0";
  const s = String(raw).trim();
  if (s === "" || s === ".") return "0";
  if (s.endsWith(".")) {
    const head = s.slice(0, -1);
    return head === "" ? "0" : head;
  }
  return s;
};

const displayHrsStored = (raw) => {
  if (raw === undefined || raw === null || raw === "") return "0";
  return String(raw);
};

const MechanicDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skipBillingStatusFromApiRef = useRef(hadBillingSetupReturnOnLoad());
  const billingReturn = parseBillingSetupReturn(searchParams);
  const [billingConfirming, setBillingConfirming] = useState(billingReturn.isSuccessReturn);

  const [shop, setShop] = useState({ name: "", address: {} });
  const [linkingMessage, setLinkingMessage] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [biddingRequests, setBiddingRequests] = useState([]);
  const [announcementRequests, setAnnouncementRequests] = useState([]);
  const [directRequests, setDirectRequests] = useState([]);
  // Unified service requests array
  const [allServiceRequests, setAllServiceRequests] = useState([]);
  const [isExtendedHistoryActive, setIsExtendedHistoryActive] = useState(false);
  const [
    isExtendedHistoryPlanAvailableBool,
    setIsExtendedHistoryPlanAvailableBool,
  ] = useState(false);

  // Calculate request counts
  const directRequestCount = useMemo(
    () =>
      allServiceRequests.filter((req) => req.request_display_type === "Direct")
        .length,
    [allServiceRequests],
  );

  const broadcastRequestCount = useMemo(
    () =>
      allServiceRequests.filter(
        (req) => req.request_display_type === "Broadcast",
      ).length,
    [allServiceRequests],
  );

  // Function to determine if a request needs attention
  const needsAttention = (request) => {
    // Check if shop has submitted a quote
    const shopBid = request.bids?.find((bid) => bid.shop_name === shop.name);
    const hasQuote = !!shopBid;

    // Check if there's a new message from customer
    const hasNewMessage = request.has_new_message || false;

    // Check for any customer response that needs attention
    const quoteRejected = shopBid?.status === "Rejected";
    const customerAccepted = shopBid?.bid_status === "customer_accepted";

    // Needs attention if:
    // 1. No quote submitted yet
    // 2. New message from customer
    // 3. Quote was rejected (needs resubmission or cancellation)
    // 4. Customer accepted quote (needs shop confirmation)
    return !hasQuote || hasNewMessage || quoteRejected || customerAccepted;
  };

  // Calculate attention counts
  const totalAttentionCount = useMemo(
    () => allServiceRequests.filter(needsAttention).length,
    [allServiceRequests, shop],
  );

  const directAttentionCount = useMemo(
    () =>
      allServiceRequests.filter(
        (req) => req.request_display_type === "Direct" && needsAttention(req),
      ).length,
    [allServiceRequests, shop],
  );

  const broadcastAttentionCount = useMemo(
    () =>
      allServiceRequests.filter(
        (req) =>
          req.request_display_type === "Broadcast" && needsAttention(req),
      ).length,
    [allServiceRequests, shop],
  );
  const [inProgressServices, setInProgressServices] = useState([]);
  const [pastBids, setPastBids] = useState([]);
  // const [allVisitors, setAllVisitors] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [viewUser, setViewUser] = useState(false);
  const [bidAmounts, setBidAmounts] = useState({});
  const [completedServices, setCompletedServices] = useState([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionRequestId, setCompletionRequestId] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedMechanics, setSelectedMechanics] = useState([]);
  const [serviceCosts, setServiceCosts] = useState({});
  const [completionNotes, setCompletionNotes] = useState("");
  const [selectedCompleted, setSelectedCompleted] = useState(null);
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  // Platform ratings state
  const [platformRatings, setPlatformRatings] = useState({
    customer_rating: null, // Mechanic rates customer (editable)
    mechanic_rating: null, // Customer rates mechanic (display only)
    platform_rating: null, // User rates Autoshops.com (editable)
  });
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [selectedRatingType, setSelectedRatingType] = useState(null); // 'customer', 'platform', or null
  const [ratingModalRating, setRatingModalRating] = useState(0);
  const [ratingModalComment, setRatingModalComment] = useState("");
  const [savingRating, setSavingRating] = useState(false);
  const [customerRatingModalOpen, setCustomerRatingModalOpen] = useState(false);
  const [selectedMechanicForRating, setSelectedMechanicForRating] =
    useState(null);
  const [cancelQuoteDialogOpen, setCancelQuoteDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [cancelQuoteReason, setCancelQuoteReason] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectBidId, setRejectBidId] = useState(null);
  const [rejectMessage, setRejectMessage] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [bidMessages, setBidMessages] = useState({});
  const [filterType, setFilterType] = useState("all");
  const [filterValue, setFilterValue] = useState("");
  const [user, setUser] = useState(null);

  // NEW: Announcement flow state
  const [shopTimeSlots, setShopTimeSlots] = useState({}); // { requestId: [{start: '9:00', end: '11:00'}, ...] }
  const [dropoffDates, setDropoffDates] = useState({}); // { requestId: [{date: 'YYYY-MM-DD', dropoff_time: 'HH:mm', pickup_time: 'HH:mm'}, ...] }
  const [loading, setLoading] = useState(true);
  const [radius, setRadius] = useState(25); // default to 25 miles
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [billingReadyForQuotes, setBillingReadyForQuotes] = useState(false);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [pendingBillingRequestId, setPendingBillingRequestId] = useState(null);
  const [billingGateMessage, setBillingGateMessage] = useState(null);
  const [billingSetupMessage, setBillingSetupMessage] = useState(null);
  const [rateChangeModalOpen, setRateChangeModalOpen] = useState(false);
  const [rateNotice, setRateNotice] = useState(null);
  const [pendingBidRequestId, setPendingBidRequestId] = useState(null);
  const [pendingConfirmRequestId, setPendingConfirmRequestId] = useState(null);
  const [hasBiddingEnabled, setHasBiddingEnabled] = useState(false);
  const [hasAnnouncementsEnabled, setHasAnnouncementsEnabled] = useState(false);
  const [hasExtendedServiceHistory, setHasExtendedServiceHistory] =
    useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [isExtendedHistoryPlanAvailable, setIsExtendedHistoryPlanAvailable] =
    useState(true); // Default to true for backward compatibility
  const [serviceFilter, setServiceFilter] = useState(
    localStorage.getItem("serviceFilter") || "all",
  );
  const [newMileage, setNewMileage] = useState("");
  const [obdCodes, setObdCodes] = useState([]);
  const [selectedObdCode, setSelectedObdCode] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState({});
  const [proposedStartTimes, setProposedStartTimes] = useState({});
  const [proposedHours, setProposedHours] = useState({}); // { requestId: '9' }
  const [proposedMinutes, setProposedMinutes] = useState({}); // { requestId: '00' }
  const [proposedAmpm, setProposedAmpm] = useState({}); // { requestId: 'AM' }
  const [jobDurations, setJobDurations] = useState({});
  const [typedCode, setTypedCode] = useState("");
  const [completionCarId, setCompletionCarId] = useState("");
  const [completionUserId, setCompletionUserId] = useState("");
  const [shopReviews, setShopReviews] = useState(null);
  const [selectedCounterDate, setSelectedCounterDate] = useState(null);
  const [counterAvailableDates, setCounterAvailableDates] = useState([]);
  const [counterStartTime, setCounterStartTime] = useState(null);
  const [counterEndTime, setCounterEndTime] = useState(null);
  const [counterOffer, setCounterOffer] = useState({});
  const [extendedHistoryModalOpen, setExtendedHistoryModalOpen] =
    useState(false);
  const [extendedHistoryPrice, setExtendedHistoryPrice] = useState(null);
  const [messageCenterOpen, setMessageCenterOpen] = useState(false);
  const [messagesSummary, setMessagesSummary] = useState([]);
  const [loadingMessagesSummary, setLoadingMessagesSummary] = useState(false);
  const [messagesSummaryLoaded, setMessagesSummaryLoaded] = useState(false);
  const unreadMessageCount = useMemo(
    () =>
      (messagesSummary || []).reduce(
        (sum, convo) => sum + (convo.unread_count || 0),
        0,
      ),
    [messagesSummary],
  );
  const unreadByRequest = useMemo(() => {
    const map = new Map();
    (messagesSummary || []).forEach((convo) => {
      map.set(convo.service_request_id, convo.unread_count || 0);
    });
    return map;
  }, [messagesSummary]);
  const hasUnreadMessages = (requestId) =>
    (unreadByRequest.get(requestId) || 0) > 0;
  const activeConversations = useMemo(
    () =>
      (messagesSummary || []).filter((convo) => (convo.unread_count || 0) > 0),
    [messagesSummary],
  );
  const historyConversations = useMemo(
    () =>
      (messagesSummary || []).filter(
        (convo) => (convo.unread_count || 0) === 0,
      ),
    [messagesSummary],
  );

  // Alternative time components (12-hour format dropdowns)
  const [altTimeHour, setAltTimeHour] = useState("9");
  const [altTimeMinute, setAltTimeMinute] = useState("00");
  const [altTimeAmpm, setAltTimeAmpm] = useState("AM");
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [approvalPendingModalOpen, setApprovalPendingModalOpen] =
    useState(false);
  const [reviewConversationOpen, setReviewConversationOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewComments, setReviewComments] = useState([]);
  const [serviceHistoryModalOpen, setServiceHistoryModalOpen] = useState(false);
  const [selectedServiceRequest, setSelectedServiceRequest] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [expandedDirectRequest, setExpandedDirectRequest] = useState(null);

  // Shop switcher and co-owner requests state
  const [currentShop, setCurrentShop] = useState(null); // Currently selected shop
  const [allShops, setAllShops] = useState([]); // All shops mechanic has access to
  const [showShopSwitcher, setShowShopSwitcher] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]); // Co-owner requests
  const [expandedServiceRequests, setExpandedServiceRequests] = useState({}); // { requestId: boolean }
  const [serviceRequestsPanelExpanded, setServiceRequestsPanelExpanded] = useState(false);
  const [directRequestsPanelExpanded, setDirectRequestsPanelExpanded] = useState(false);
  const [broadcastRequestsPanelExpanded, setBroadcastRequestsPanelExpanded] = useState(false);
  const [conversationModalOpen, setConversationModalOpen] = useState(false);
  const [selectedConversationRequest, setSelectedConversationRequest] =
    useState(null);
  const [conversationComments, setConversationComments] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);

  // Store conversation threads per service request ID to prevent bleed
  const [conversationThreadsMap, setConversationThreadsMap] = useState({});

  // Open conversation modal for direct service request
  const handleOpenConversationModal = async (request) => {
    setSelectedConversationRequest(request);
    setLoadingConversation(true);
    setConversationModalOpen(true);

    const requestId = request.id;

    // Check if we already have this thread cached
    if (conversationThreadsMap[requestId]) {
      setConversationComments(conversationThreadsMap[requestId]);
      setLoadingConversation(false);
      return;
    }

    try {
      const response = await fetch(
        `${getApiBase()}/api/messages/${requestId}`,
        { credentials: "include" },
      );
      if (response.ok) {
        const data = await response.json();
        const comments = data.comments || [];
        setConversationComments(comments);
        setConversationThreadsMap((prev) => ({
          ...prev,
          [requestId]: comments,
        }));
        fetchMessageSummary();
      }
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleOpenConversationFromSummary = (conversation) => {
    if (!conversation?.service_request_id) return;
    handleOpenConversationModal({
      id: conversation.service_request_id,
      user: conversation.customer_name,
      vehicle: conversation.vehicle,
    });
  };

  // Send message in conversation
  const handleSendConversationMessage = async (message) => {
    if (!selectedConversationRequest) return;

    const requestId = selectedConversationRequest.id;

    try {
      const response = await fetch(`${getApiBase()}/api/messages/shop-reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          service_request_id: requestId,
          comment: message,
        }),
      });

      if (response.ok) {
        const newComment = await response.json();
        const updatedComments = [
          ...(conversationThreadsMap[requestId] || []),
          newComment,
        ];
        setConversationComments(updatedComments);
        setConversationThreadsMap((prev) => ({
          ...prev,
          [requestId]: updatedComments,
        }));
        fetchMessageSummary();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      throw error;
    }
  };

  const roundToNearest15 = (timeStr) => {
    if (!timeStr) return "";

    const [hour, minute] = timeStr.split(":").map(Number);
    const totalMinutes = hour * 60 + minute;
    const roundedMinutes = Math.round(totalMinutes / 15) * 15;

    const adjustedHour = Math.floor(roundedMinutes / 60) % 24;
    const adjustedMinute = roundedMinutes % 60;

    return `${String(adjustedHour).padStart(2, "0")}:${String(
      adjustedMinute,
    ).padStart(2, "0")}`;
  };

  const fetchObdCodes = async () => {
    try {
      const res = await fetch(`${getApiBase()}/api/obdcodes/all`);
      const data = await res.json();
      setObdCodes(data.codes || []);
    } catch (err) {
      console.error("Failed to load OBD2 codes:", err);
    }
  };

  const normalizeMessageSummaryResponse = (payload) => {
    if (!payload) return [];

    let conversations = [];

    if (Array.isArray(payload)) {
      conversations = payload;
    } else if (Array.isArray(payload.conversations)) {
      conversations = payload.conversations;
    } else if (Array.isArray(payload.summary)) {
      conversations = payload.summary;
    } else if (Array.isArray(payload.data)) {
      conversations = payload.data;
    } else if (payload.data) {
      if (Array.isArray(payload.data.conversations)) {
        conversations = payload.data.conversations;
      } else if (Array.isArray(payload.data.summary)) {
        conversations = payload.data.summary;
      }
    }

    if (!Array.isArray(conversations)) return [];

    return conversations.map((item) => {
      if (!item || typeof item !== "object") return item;

      const vehicle =
        item.vehicle ||
        item.car_details ||
        item.car ||
        [item.year, item.make, item.model].filter(Boolean).join(" ") ||
        null;

      let lastMessage = item.last_message || null;
      if (!lastMessage) {
        if (item.last_message_preview || item.last_message_time) {
          lastMessage = {
            snippet: item.last_message_preview || item.last_message || "",
            author_type:
              item.last_message_author_type ||
              item.last_message_author ||
              "customer",
            created_at:
              item.last_message_time ||
              item.updated_at ||
              item.inserted_at ||
              null,
          };
        }
      } else if (!lastMessage.snippet && lastMessage.message) {
        lastMessage = {
          ...lastMessage,
          snippet: lastMessage.message,
        };
      }

      return {
        ...item,
        vehicle,
        last_message: lastMessage,
      };
    });
  };

  const fetchMessageSummary = async (options = {}) => {
    const { showLoader = false } = options;
    const shouldShowLoader = showLoader || !messagesSummaryLoaded;
    if (shouldShowLoader) setLoadingMessagesSummary(true);
    try {
      const res = await fetch(`${getApiBase()}/api/messages/shop/summary`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setMessagesSummary(normalizeMessageSummaryResponse(data));
        setMessagesSummaryLoaded(true);
      } else {
        console.error(
          "Failed to load message summary:",
          res.status,
          res.statusText,
        );
        setMessagesSummary([]);
      }
    } catch (err) {
      console.error("Failed to load message summary:", err);
    } finally {
      if (shouldShowLoader) setLoadingMessagesSummary(false);
      if (!messagesSummaryLoaded) {
        setMessagesSummaryLoaded(true);
      }
    }
  };

  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // zero-padded
  const day = String(today.getDate()).padStart(2, "0"); // zero-padded

  const autoshop_confirm_date = `${year}-${month}-${day}`;

  const storedUser = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchDashboardData();

    const urlParams = new URLSearchParams(window.location.search);

    // Check for Google Business linking result
    const linked = urlParams.get("linked");
    if (linked === "success") {
      setLinkingMessage({
        type: "success",
        text: "✅ Google Business account linked successfully! Your confidence score has been updated.",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (linked === "error") {
      setLinkingMessage({
        type: "error",
        text: "❌ Failed to link Google Business account. Please try again.",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [navigate]);

  function getWeekNumber(date) {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const jan4 = new Date(target.getFullYear(), 0, 4);
    const diff = target.getTime() - jan4.getTime();
    return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
  }

  // Fetch visitors with server-side filtering when filter changes
  useEffect(() => {
    if (!user) return;
    fetchVisitors();
  }, [filterType, filterValue, user]);

  const collectQuoteDraft = (requestId) => ({
    bidAmount: bidAmounts[requestId],
    bidMessage: bidMessages[requestId],
    selectedSlot: selectedSlot[requestId],
    proposedStart: proposedStartTimes[requestId],
    jobDuration: jobDurations[requestId],
    shopTimeSlots: shopTimeSlots[requestId],
    dropoffDates: dropoffDates[requestId],
    counterAvailableDates: counterAvailableDates[requestId],
    counterOffer: counterOffer[requestId],
    proposedHours: proposedHours[requestId],
    proposedMinutes: proposedMinutes[requestId],
    proposedAmpm: proposedAmpm[requestId],
  });

  const restoreQuoteDraft = (requestId, draft) => {
    if (!requestId || !draft) return;

    if (draft.bidAmount !== undefined && draft.bidAmount !== "") {
      setBidAmounts((prev) => ({ ...prev, [requestId]: draft.bidAmount }));
    }
    if (draft.bidMessage) {
      setBidMessages((prev) => ({ ...prev, [requestId]: draft.bidMessage }));
    }
    if (draft.selectedSlot) {
      setSelectedSlot((prev) => ({
        ...prev,
        [requestId]: normalizeSelectedSlots(draft.selectedSlot),
      }));
    }
    if (draft.proposedStart) {
      setProposedStartTimes((prev) => ({ ...prev, [requestId]: draft.proposedStart }));
    }
    if (draft.jobDuration !== undefined) {
      setJobDurations((prev) => ({ ...prev, [requestId]: draft.jobDuration }));
    }
    if (draft.shopTimeSlots?.length) {
      setShopTimeSlots((prev) => ({ ...prev, [requestId]: draft.shopTimeSlots }));
    }
    if (draft.dropoffDates?.length) {
      setDropoffDates((prev) => ({ ...prev, [requestId]: draft.dropoffDates }));
    }
    if (draft.counterAvailableDates?.length) {
      setCounterAvailableDates((prev) => ({
        ...prev,
        [requestId]: draft.counterAvailableDates,
      }));
    }
    if (draft.counterOffer) {
      setCounterOffer((prev) => ({ ...prev, [requestId]: draft.counterOffer }));
    }
    if (draft.proposedHours) {
      setProposedHours((prev) => ({ ...prev, [requestId]: draft.proposedHours }));
    }
    if (draft.proposedMinutes) {
      setProposedMinutes((prev) => ({ ...prev, [requestId]: draft.proposedMinutes }));
    }
    if (draft.proposedAmpm) {
      setProposedAmpm((prev) => ({ ...prev, [requestId]: draft.proposedAmpm }));
    }

    setExpandedServiceRequests((prev) => ({ ...prev, [requestId]: true }));
  };

  const applyQuoteResumeAfterBilling = () => {
    const saved = loadQuoteResumeDraft();
    if (!saved?.requestId || !saved.draft) return false;
    if (saved.shopId && shop?.id && Number(saved.shopId) !== Number(shop.id)) {
      return false;
    }

    const requestId = saved.requestId;
    restoreQuoteDraft(requestId, saved.draft);
    clearQuoteResumeDraft();
    setPendingBillingRequestId(null);

    setServiceRequestsPanelExpanded(true);
    if (saved.requestDisplayType === "Broadcast") {
      setBroadcastRequestsPanelExpanded(true);
    } else if (saved.requestDisplayType === "Direct") {
      setDirectRequestsPanelExpanded(true);
    }

    const sectionId =
      saved.requestDisplayType === "Broadcast"
        ? "broadcast-requests-section"
        : saved.requestDisplayType === "Direct"
          ? "direct-requests-section"
          : "service-requests-section";

    window.setTimeout(() => {
      document
        .getElementById(sectionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        document
          .getElementById(`service-request-${requestId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }, 450);

    return true;
  };

  const openBillingRequiredModal = (requestId = null) => {
    if (requestId) {
      setPendingBillingRequestId(requestId);
      const requestMeta = allServiceRequests.find((r) => r.id === requestId);
      saveQuoteResumeDraft(
        requestId,
        shop?.id,
        collectQuoteDraft(requestId),
        requestMeta?.request_display_type || null,
      );
    } else {
      setPendingBillingRequestId(null);
    }
    setBillingModalOpen(true);
  };

  useBillingSetupReturn({
    mechanicId: user?.id,
    shopId: shop?.id,
    returnPath: buildDashboardBillingReturnPath(
      shop?.id || searchParams.get("shopId"),
      loadQuoteResumeDraft()?.requestId || searchParams.get("resumeQuote"),
    ),
    preserveShopId: true,
    skipBillingStatusFromApiRef,
    onStart: () => setBillingConfirming(true),
    onProgress: () => {
      setBillingSetupMessage({
        type: "info",
        text: "Saving your payment method...",
      });
    },
    onComplete: (result) => {
      setBillingConfirming(false);
      if (result.billingReadyForQuotes) {
        setBillingReadyForQuotes(true);
        setBillingGateMessage(null);
        setBillingModalOpen(false);
        sessionStorage.removeItem("billing_setup_return_failed");

        const resumed = applyQuoteResumeAfterBilling();
        const baseMessage = resumed
          ? "Payment method saved. Your quote is restored — click Submit to send it."
          : "Payment method saved. You can submit quotes on the Free plan.";
        setBillingSetupMessage({
          type: "success",
          text: isVercelFeatureBranchDeploy()
            ? `${baseMessage} ${PREVIEW_BILLING_WEBHOOK_NOTICE}`
            : baseMessage,
        });
        return;
      }
      const baseMessage =
        "Payment received. If quoting is still blocked, refresh or contact support.";
      setBillingSetupMessage({
        type: "warning",
        text: isVercelFeatureBranchDeploy()
          ? `${baseMessage} ${PREVIEW_BILLING_WEBHOOK_NOTICE}`
          : baseMessage,
      });
    },
    onError: (err) => {
      setBillingConfirming(false);
      sessionStorage.setItem("billing_setup_return_failed", "1");
      setBillingSetupMessage({
        type: "error",
        text: err.message || "Could not confirm payment setup.",
      });
    },
  });

  useEffect(() => {
    if (loading || !shop?.id || !billingReadyForQuotes) return;
    if (!hasQuoteResumePending()) return;
    applyQuoteResumeAfterBilling();
  }, [loading, shop?.id, billingReadyForQuotes]);

  // Shop-level card reminder: any Owner, only when this shop has no card on file.
  useEffect(() => {
    if (billingConfirming) return;
    if (sessionStorage.getItem("billing_setup_return_failed")) return;
    if (loading || !shop?.id) return;
    if (user?.role !== "Owner") return;
    if (shop?.status && shop.status !== "Active") return;
    if (billingReadyForQuotes) return;
    if (sessionStorage.getItem("billing_setup_dismissed")) return;
    setBillingModalOpen(true);
  }, [loading, user?.role, shop?.id, shop?.status, billingReadyForQuotes, billingConfirming]);

  useEffect(() => {
    if (!activeSubscriptions || activeSubscriptions.length === 0) {
      setIsExtendedHistoryActive(false);
      return;
    }

    const extendedHistorySub = activeSubscriptions.find(
      (sub) =>
        sub.name === "addon_service_history" ||
        sub.name?.includes("service_history") ||
        sub.name?.includes("extended"),
    );

    const isActive = !!(
      extendedHistorySub && extendedHistorySub.is_active !== 0
    );

    setIsExtendedHistoryActive(isActive);
    setIsExtendedHistoryPlanAvailableBool(!!isExtendedHistoryPlanAvailable);
  }, [activeSubscriptions, isExtendedHistoryPlanAvailable]);

  // Fetch all shops for shop switcher
  const fetchAllShops = async () => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/mechanicDashboard/shops`,
        {
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.success) {
        setAllShops(data.shops || []);
      } else {
        console.error("Dashboard error:", data.error);
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  };

  // Fetch pending co-owner requests
  const fetchPendingRequests = async () => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/owner-join-requests/pending`,
        {
          credentials: "include",
        },
      );

      const data = await response.json();
      setPendingRequests(data.requests || []);
    } catch (error) {
      console.error("Error fetching pending requests:", error);
    }
  };

  // Shop switch handler
  const handleShopSwitch = async (shopId) => {
    try {
      const response = await fetch(
        `${getApiBase()}/api/mechanicDashboard/primary-shop`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ shop_id: shopId }),
        },
      );

      const data = await response.json();

      if (data.success) {
        // Reload dashboard data
        window.location.reload();
      } else {
        alert("Error switching shop: " + data.error);
      }
    } catch (error) {
      console.error("Error switching shop:", error);
      alert("Failed to switch shop");
    }
  };

  // Approve co-owner request
  const handleApproveRequest = async (requestId) => {
    const confirmed = window.confirm(
      "Are you sure you want to approve this co-owner request?\n\nThe requester will receive an invite link to create their account and join your shop.",
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${getApiBase()}/api/owner-join-requests/${requestId}/approve`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      const data = await response.json();

      if (data.success) {
        alert(
          "Request approved! An invite link has been sent to the requester.",
        );
        // Remove from pending list
        setPendingRequests(pendingRequests.filter((r) => r.id !== requestId));
      } else {
        alert("Error: " + data.error);
      }
    } catch (error) {
      console.error("Error approving request:", error);
      alert("Failed to approve request");
    }
  };

  // Reject co-owner request
  const handleRejectRequest = async (requestId) => {
    const reason = prompt("Rejection reason (optional):");

    try {
      const response = await fetch(
        `${getApiBase()}/api/owner-join-requests/${requestId}/reject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ rejection_reason: reason || null }),
        },
      );

      const data = await response.json();

      if (data.success) {
        alert("Request rejected");
        // Remove from pending list
        setPendingRequests(pendingRequests.filter((r) => r.id !== requestId));
      } else {
        alert("Error: " + data.error);
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert("Failed to reject request");
    }
  };

  // Refetch dashboard when service filter changes
  useEffect(() => {
    if (!user) return;
    // Save filter preference
    localStorage.setItem("serviceFilter", serviceFilter);
    // Refetch dashboard data with new filter
    fetchDashboardData();
  }, [serviceFilter]);

  // Fetch Extended Service History plan price when modal opens
  useEffect(() => {
    if (!extendedHistoryModalOpen) return;

    const fetchExtendedHistoryPrice = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/stripe/plans`);
        if (response.ok) {
          const data = await response.json();
          const plans = data.plans || [];
          // Find the Extended Service History plan (usually named "Extended Service History" or similar)
          const extendedHistoryPlan = plans.find(
            (p) =>
              p.plan_type === "addon" &&
              (p.name?.toLowerCase().includes("extended") ||
                p.display_name?.toLowerCase().includes("extended") ||
                p.name?.toLowerCase().includes("service history") ||
                p.display_name?.toLowerCase().includes("service history")),
          );
          if (extendedHistoryPlan) {
            setExtendedHistoryPrice(
              parseFloat(extendedHistoryPlan.monthly_price || 0),
            );
          }
        }
      } catch (error) {
        console.error("Error fetching Extended Service History price:", error);
      }
    };

    fetchExtendedHistoryPrice();
  }, [extendedHistoryModalOpen]);

  // Pre-fill bid form with last bid's selections for rebidding (bidding + announcement so "Customer Accepted" chip stays highlighted)
  const allBiddableRequests = [
    ...(biddingRequests || []),
    ...(announcementRequests || []),
  ];
  useEffect(() => {
    if (allBiddableRequests.length === 0 || !shop.name) return;

    const newBidAmounts = { ...bidAmounts };
    const newSelectedSlots = { ...selectedSlot };
    const newProposedStartTimes = { ...proposedStartTimes };
    const newJobDurations = { ...jobDurations };

    allBiddableRequests.forEach((req) => {
      // Find this shop's last bid on this request
      const myLastBid = req.bids?.find((bid) => bid.shop_name === shop.name);

      if (myLastBid) {
        // Pre-fill bid price
        if (!newBidAmounts[req.id]) {
          newBidAmounts[req.id] = myLastBid.offer_price;
        }

        // Pre-fill proposed start time
        if (!newProposedStartTimes[req.id] && myLastBid.proposed_time) {
          newProposedStartTimes[req.id] = myLastBid.proposed_time;
        }

        // Pre-fill job duration
        if (!newJobDurations[req.id] && myLastBid.estimated_duration) {
          newJobDurations[req.id] = myLastBid.estimated_duration;
        }

        // Pre-select the date chip (for "Quote Submitted" and "Customer Accepted" so the chosen slot stays highlighted)
        if (
          !newSelectedSlots[req.id] &&
          (myLastBid.selected_available_date ||
            myLastBid.customer_selected_slot_details) &&
          req.availableDates?.length > 0
        ) {
          const details = myLastBid.customer_selected_slot_details;
          const bidDateOnly = (
            details?.available_date ||
            details?.date ||
            myLastBid.selected_available_date ||
            ""
          )
            .toString()
            .split("T")[0];
          const bidIsDropoff = myLastBid.is_dropoff === true;
          if (!bidDateOnly) return;
          const matchingSlot =
            req.availableDates.find((slot) => {
              const slotDate = slot?.date ?? slot?.available_date;
              const slotDateOnly = (slotDate || "").toString().split("T")[0];
              if (!slotDateOnly || slotDateOnly !== bidDateOnly) return false;
              const slotIsDropoff =
                slot.is_dropoff || (!slot.start_time && !slot.end_time);
              return slotIsDropoff === bidIsDropoff;
            }) ||
            req.availableDates.find((slot) => {
              const slotDate = slot?.date ?? slot?.available_date;
              const slotDateOnly = (slotDate || "").toString().split("T")[0];
              return slotDateOnly && slotDateOnly === bidDateOnly;
            });
          if (matchingSlot) {
            const index = req.availableDates.indexOf(matchingSlot);
            newSelectedSlots[req.id] = [{ slot: matchingSlot, index }];
          }
        }
      }
    });

    setBidAmounts(newBidAmounts);
    setSelectedSlot(newSelectedSlots);
    setProposedStartTimes(newProposedStartTimes);
    setJobDurations(newJobDurations);
  }, [biddingRequests, announcementRequests, shop.name]);

  const fetchVisitors = async () => {
    try {
      let visitorsUrl = `${getApiBase()}/api/mechanicDashboard/visitors`;

      // Build query parameters based on filter type
      const params = new URLSearchParams();

      if (filterType === "day" && filterValue) {
        // Single day - use explicit start/end dates
        const selectedDate = new Date(filterValue);
        const startDate = new Date(selectedDate.setHours(0, 0, 0, 0));
        const endDate = new Date(selectedDate.setHours(23, 59, 59, 999));

        params.append("startDate", startDate.toISOString());
        params.append("endDate", endDate.toISOString());
      } else if (filterType === "week" && filterValue) {
        // Week - calculate start/end from week number
        const [yearStr, weekStr] = filterValue.split("-W");
        const year = parseInt(yearStr);
        const week = parseInt(weekStr);

        // Simple approximation: week * 7 days from Jan 1
        const startDate = new Date(year, 0, 1 + (week - 1) * 7);
        const endDate = new Date(year, 0, 1 + week * 7);

        params.append("startDate", startDate.toISOString());
        params.append("endDate", endDate.toISOString());
      } else if (filterType === "month" && filterValue) {
        // Month - use first and last day of month
        const [year, month] = filterValue.split("-");
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(
          parseInt(year),
          parseInt(month),
          0,
          23,
          59,
          59,
        );

        params.append("startDate", startDate.toISOString());
        params.append("endDate", endDate.toISOString());
      } else if (filterType === "year" && filterValue) {
        // Year - Jan 1 to Dec 31
        const year = parseInt(filterValue);
        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31, 23, 59, 59);

        params.append("startDate", startDate.toISOString());
        params.append("endDate", endDate.toISOString());
      } else if (filterType === "all") {
        // No filtering - fetch all
        params.append("period", "all");
      }

      if (params.toString()) {
        visitorsUrl += `?${params.toString()}`;
      }

      const visitorsRes = await fetch(visitorsUrl, { credentials: "include" });
      if (visitorsRes.ok) {
        const visitorsData = await visitorsRes.json();
        // setAllVisitors(visitorsData);
        setVisitors(visitorsData);
      }
    } catch (err) {
      console.error("Failed to fetch visitors:", err);
    }
  };

  const fetchDashboardData = async () => {
    if (!storedUser || !["Mechanic", "Owner"].includes(storedUser.role)) {
      navigate("/login?role=Mechanic&redirect=/mechanic-dashboard");
      return;
    }
    setUser(storedUser);

    try {
      const dashboardRes = await fetch(
        `${getApiBase()}/api/mechanicDashboard/dashboard?radius=${radius}&serviceFilter=${serviceFilter}`,
        {
          credentials: "include",
        },
      );

      if (!dashboardRes.ok) {
        throw new Error("Failed to load dashboard core data");
      }

      const dashboardData = await dashboardRes.json();

      // Set SAAS subscription status
      setHasPaidSubscription(dashboardData.hasPaidSubscription || false);
      if (!skipBillingStatusFromApiRef.current) {
        setBillingReadyForQuotes(dashboardData.billingReadyForQuotes || false);
      }
      setBillingGateMessage(dashboardData.billingGateMessage || null);
      setHasBiddingEnabled(dashboardData.hasBiddingEnabled || false);
      setHasAnnouncementsEnabled(
        dashboardData.hasAnnouncementsEnabled || false,
      );
      setHasExtendedServiceHistory(
        dashboardData.hasExtendedServiceHistory || false,
      );
      setActiveSubscriptions(dashboardData.activeSubscriptions || []);

      // Set core state
      setShop(dashboardData.shop);
      setCurrentShop(dashboardData.shop); // Set current shop for switcher
      setMechanics(dashboardData.mechanics);
      setServiceHistory(dashboardData.serviceHistory);
      setBiddingRequests(dashboardData.biddingRequests || []);
      setAnnouncementRequests(dashboardData.announcementRequests || []);
      setDirectRequests(dashboardData.directRequests || []);

      // Combine direct requests and announcements into unified array
      const directWithType = (dashboardData.directRequests || []).map(
        (req) => ({
          ...req,
          request_display_type: "Direct",
        }),
      );
      const broadcastWithType = (dashboardData.announcementRequests || []).map(
        (req) => ({
          ...req,
          request_display_type: "Broadcast",
        }),
      );
      setAllServiceRequests([...directWithType, ...broadcastWithType]);
      setInProgressServices(dashboardData.inProgressServices || []);
      setCompletedServices(dashboardData.completedServices);
      setPastBids(dashboardData.pastBids || []);

      // Fetch visitors separately (will use filter settings)
      await fetchVisitors();

      // Fetch all shops for shop switcher
      await fetchAllShops();

      // Fetch pending co-owner requests
      await fetchPendingRequests();

      // Fetch extended history plan availability
      try {
        const plansRes = await fetch(`${getApiBase()}/api/stripe/plans`, {
          credentials: "include",
        });
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          const plans = plansData.plans || [];
          const extendedHistoryPlan = plans.find(
            (p) =>
              p.plan_type === "addon" &&
              (p.name?.toLowerCase().includes("extended") ||
                p.display_name?.toLowerCase().includes("extended") ||
                p.name?.toLowerCase().includes("service history") ||
                p.display_name?.toLowerCase().includes("service history")),
          );
          // Plan is available if it exists and is_active !== 0
          setIsExtendedHistoryPlanAvailable(
            extendedHistoryPlan ? extendedHistoryPlan.is_active !== 0 : false,
          );
        }
      } catch (err) {
        console.error(
          "Error fetching extended history plan availability:",
          err,
        );
        // Default to false on error to be safe
        setIsExtendedHistoryPlanAvailable(false);
      }

      // Check if we should show the status modal (first login or status changed)
      const shopStatus = dashboardData.shop?.status;
      const hasSeenStatusModal = localStorage.getItem(
        `statusModal_${shopStatus}_seen`,
      );

      if (shopStatus && shopStatus !== "Active" && !hasSeenStatusModal) {
        setStatusModalOpen(true);
      }

      const shopId = dashboardData?.shop?.id;
      if (shopId) {
        try {
          const reviewsRes = await fetch(
            `${getApiBase()}/api/services/shops/${shopId}/reviews?limit=10&offset=0`,
            { credentials: "include" },
          );
          if (reviewsRes.ok) {
            const { avg_rating, review_count, reviews } =
              await reviewsRes.json();
            // TASK-735: client-side guard — same Google review must not render twice
            // (concurrent populate race; BE also dedupes + unique index).
            const seen = new Set();
            const uniqueReviews = (reviews || []).filter((r) => {
              const key =
                r.google_review_id != null
                  ? `gid:${r.google_review_id}`
                  : `meta:${r.first_name || ""}|${r.last_name || ""}|${r.created_at || ""}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            setShopReviews({
              avg_rating,
              review_count,
              reviews: uniqueReviews,
            });
          } else {
            console.warn("Reviews fetch failed:", reviewsRes.status);
            setShopReviews({ avg_rating: null, review_count: 0, reviews: [] });
          }
        } catch (e) {
          console.warn("Reviews fetch error:", e);
          setShopReviews({ avg_rating: null, review_count: 0, reviews: [] });
        }
      } else {
        setShopReviews({ avg_rating: null, review_count: 0, reviews: [] });
      }

      fetchMessageSummary();
      setLoading(false);
    } catch (err) {
      console.error(err);
      navigate("/login?role=Mechanic&redirect=/mechanic-dashboard");
    }
  };

  useEffect(() => {
    const fetchObdCodes = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/obdcodes/all`);
        const data = await res.json();
        setObdCodes(data.codes || []);
      } catch (err) {
        console.error("Failed to load OBD2 codes:", err);
      }
    };

    fetchObdCodes();
  }, []);

  useEffect(() => {
    fetchMessageSummary();
    const interval = setInterval(() => {
      fetchMessageSummary();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch comments for a review (used for preview)
  const fetchReviewComments = async (serviceRequestId) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/comments/${serviceRequestId}`,
        { credentials: "include" },
      );

      if (res.ok) {
        const data = await res.json();
        return data.comments || [];
      }
      return [];
    } catch (err) {
      console.error("Error fetching comments:", err);
      return [];
    }
  };

  // Open review conversation modal
  const openReviewConversation = async (
    review,
    serviceRequestId,
    existingComments = null,
  ) => {
    setSelectedReview({ ...review, service_request_id: serviceRequestId });
    setReviewConversationOpen(true);
    setReplyText("");

    // Use existing comments if provided, otherwise fetch
    if (existingComments) {
      setReviewComments(existingComments);
    } else {
      setLoadingComments(true);
      const comments = await fetchReviewComments(serviceRequestId);
      setReviewComments(comments);
      setLoadingComments(false);
    }
  };

  // Submit shop reply
  const submitShopReply = async () => {
    if (!replyText.trim()) return;

    setSubmittingReply(true);
    try {
      const res = await fetch(`${getApiBase()}/api/comments/shop-reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          service_request_id: selectedReview.service_request_id,
          comment: replyText.trim(),
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to submit reply: ${text}`);
      }

      const newComment = await res.json();
      setReviewComments((prev) => [...prev, newComment]);
      setReplyText("");
      alert("Reply posted successfully!");
    } catch (err) {
      console.error("Error submitting reply:", err);
      alert(err.message);
    } finally {
      setSubmittingReply(false);
    }
  };

  const fetchAndSaveObdCode = async (code) => {
    try {
      const res = await fetch(`${getApiBase()}/api/obdcodes?code=${code}`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fetch failed");

      setObdCodes((prev) => [...prev, { code, description: data.description }]);
      return data.description;
    } catch (err) {
      console.error("❌ Error saving OBD2:", err.message);
    }
  };

  // ✅ Add custom typed code via PLUS button
  const handleAddCustomCode = async () => {
    const code = typedCode.trim().toUpperCase();
    if (!code) return;

    // ✅ Prevent duplicates in selection
    if (selectedObdCode.some((c) => c.code === code)) {
      setTypedCode("");
      return;
    }

    let desc = await fetchAndSaveObdCode(code);

    await fetchObdCodes();

    setSelectedObdCode((prev) => [...prev, { code, description: desc }]);

    setTypedCode("");
  };

  const getMessageSnippet = (text) => {
    if (!text) return "No messages yet";
    return text.length > 90 ? `${text.slice(0, 90)}…` : text;
  };

  const toggleModal = (user = null) => {
    const dash = document.getElementById("mechDash");
    if (user && typeof user === "object" && user.id) {
      setSelectedVisitor(user);
      setViewUser(true);
      dash?.classList.add("grey-out");
    } else {
      setViewUser(false);
      setSelectedVisitor(null);
      dash?.classList.remove("grey-out");
    }
  };

  const handlePriceChange = (requestId, price) => {
    setBidAmounts((prev) => ({ ...prev, [requestId]: price }));
  };

  const promptRateChangeIfNeeded = async (requestId, action = "bid") => {
    if (!shop?.id) return true;

    const notice = await fetchPlatformFeeRateNotice(getApiBase(), shop.id);
    if (!notice?.required) return true;

    setRateNotice(notice);
    if (action === "confirm") {
      setPendingConfirmRequestId(requestId);
    } else {
      setPendingBidRequestId(requestId);
    }
    setRateChangeModalOpen(true);
    return false;
  };

  const submitBid = async (requestId) => {
    if (!billingReadyForQuotes) {
      openBillingRequiredModal(requestId);
      return;
    }

    if (!(await promptRateChangeIfNeeded(requestId, "bid"))) {
      return;
    }

    const offer_price = bidAmounts[requestId];
    const message = bidMessages[requestId] || "";
    const selectedList = normalizeSelectedSlots(selectedSlot[requestId]);
    const { appointment: apptChips, dropoff: dropoffChips } =
      partitionSelectedAvailability(selectedList);
    const selected = selectedList[0] || null;
    const proposedStart = proposedStartTimes[requestId];
    const duration = jobDurations[requestId];
    let selectedDate = null;

    const counterDates = counterAvailableDates[requestId];
    console.log(counterDates);

    // Check if this is an announcement request (simpler validation - price only)
    const isAnnouncement = announcementRequests.some(
      (req) => req.id === requestId,
    );

    // Check if there's a rejected bid for this request - clear it first to allow resubmission
    const request = [...directRequests, ...announcementRequests].find(
      (req) => req.id === requestId,
    );
    if (request) {
      const shopBid = request.bids?.find((bid) => bid.shop_name === shop.name);
      if (shopBid && shopBid.status === "Rejected") {
        try {
          // Use bid_id (from backend) or id (fallback) to get the bid ID
          const bidId = shopBid.bid_id || shopBid.id;
          if (!bidId) {
            console.error(
              "Cannot clear rejected bid: bid_id not found",
              shopBid,
            );
            // Continue anyway - backend will handle if bid doesn't exist
          } else {
            const clearResponse = await fetch(
              `${getApiBase()}/api/bids/clear-rejected`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ bidId }),
              },
            );

            if (!clearResponse.ok) {
              const clearData = await clearResponse.json();
              alert(
                "Failed to clear rejected quote: " +
                  (clearData.message || "Unknown error"),
              );
              return;
            }
            // Refresh dashboard data to update UI
            await fetchDashboardData();
          }
        } catch (err) {
          alert("Error clearing rejected quote: " + err.message);
          return;
        }
      }
    }

    // Universal validation for all request types: price + at least ONE time option
    if (!offer_price) return alert("Please enter your quote price.");

    const shopSlots = shopTimeSlots[requestId] || [];
    const dropoffSlots = dropoffDates[requestId] || [];
    const counterDatesProvided = counterDates && counterDates.length > 0;
    const hasSelectedChips = selectedList.length > 0;
    const hasProposedTime = proposedStart && duration; // Old-style time proposal (for backward compatibility)

    // Must have at least ONE of: customer chips, shop time slots, alternative times, drop-off dates, or proposed time
    if (
      !hasSelectedChips &&
      shopSlots.length === 0 &&
      !counterDatesProvided &&
      dropoffSlots.length === 0 &&
      !hasProposedTime
    ) {
      return alert(
        "Please select a time from customer's availability, add your own time slot, propose alternative times, or add drop-off dates.",
      );
    }

    // Preferred-window chips need an appointment clock (proposed time or shop slots)
    if (
      apptChips.length > 0 &&
      shopSlots.length === 0 &&
      !hasProposedTime
    ) {
      return alert(
        "Set an appointment start time and duration for the preferred window you selected.",
      );
    }

    // Validate each shop slot has both start and end times (date is optional for backward compat)
    for (let i = 0; i < shopSlots.length; i++) {
      if (!shopSlots[i].start || !shopSlots[i].end) {
        return alert(`Slot ${i + 1}: Please provide both start and end times.`);
      }
    }

    if (apptChips[0]) {
      selectedDate = availabilitySlotDateOnly(apptChips[0].slot);
    } else if (selected) {
      selectedDate = availabilitySlotDateOnly(selected.slot);
    }

    try {
      const bodyData = {
        requestId,
        offer_price,
        message,
        selected_available_date: selectedDate,
        proposed_time: proposedStart,
        estimated_duration: duration,
        counter_available_date: counterDates,
      };

      // Bid-level is_dropoff: true only when offering drop-off(s) with no appointment option
      const offeringAppointment =
        apptChips.length > 0 || shopSlots.length > 0 || !!hasProposedTime;
      if (!offeringAppointment && (dropoffChips.length > 0 || dropoffSlots.length > 0)) {
        bodyData.is_dropoff = true;
      } else if (
        !offeringAppointment &&
        selected?.slot &&
        isDropoffAvailabilitySlot(selected.slot)
      ) {
        bodyData.is_dropoff = true;
      } else if (isAnnouncement && dropoffSlots.length > 0 && !offeringAppointment) {
        bodyData.is_dropoff = true;
      } else {
        bodyData.is_dropoff = false;
      }

      // Shop-offered appointment slots: existing Add time slot list + preferred chips with form clock
      const shopSlotsToSend = [...(shopTimeSlots[requestId] || [])];
      if (apptChips.length > 0 && hasProposedTime) {
        const end = endTimeFromStartAndDuration(proposedStart, duration);
        if (end) {
          for (const entry of apptChips) {
            const date = availabilitySlotDateOnly(entry.slot);
            if (!date) continue;
            const already = shopSlotsToSend.some(
              (s) =>
                String(s.date || "").split("T")[0] === date &&
                String(s.start || s.start_time || "").slice(0, 5) ===
                  String(proposedStart).slice(0, 5),
            );
            if (!already) {
              shopSlotsToSend.push({
                date,
                start: String(proposedStart).slice(0, 5),
                end,
              });
            }
          }
        }
      }
      if (shopSlotsToSend.length > 0) {
        bodyData.shop_offered_slots = shopSlotsToSend;
      }

      // Combine alternative times (appointments) and drop-off dates
      // Each date object includes its own is_dropoff flag
      const allDates = [];

      // Add alternative appointment times (preserve existing is_dropoff or default to false)
      if (counterDates && counterDates.length > 0) {
        counterDates.forEach((date) => {
          allDates.push({
            ...date,
            is_dropoff: date.is_dropoff !== undefined ? date.is_dropoff : false,
          });
        });
      }

      // Add drop-off dates from Offer drop-off UI
      if (dropoffSlots.length > 0) {
        dropoffSlots.forEach((slot) => {
          allDates.push({
            date: slot.date,
            start_time: slot.dropoff_time,
            end_time: slot.pickup_time,
            is_dropoff: true,
          });
        });
      }

      // TASK-912: selected drop-off chips → quote options (same shape as Offer drop-off)
      for (const entry of dropoffChips) {
        const date = availabilitySlotDateOnly(entry.slot);
        if (!date) continue;
        const start =
          entry.slot.start_time != null
            ? String(entry.slot.start_time).slice(0, 8)
            : null;
        const end =
          entry.slot.end_time != null
            ? String(entry.slot.end_time).slice(0, 8)
            : null;
        const dup = allDates.some(
          (d) =>
            String(d.date || "").split("T")[0] === date &&
            d.is_dropoff === true,
        );
        if (!dup) {
          allDates.push({
            date,
            start_time: start,
            end_time: end,
            is_dropoff: true,
          });
        }
      }

      // Send all dates together
      if (allDates.length > 0) {
        bodyData.counter_available_date = allDates;
      }

      const res = await fetch(`${getApiBase()}/api/bids/place`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(bodyData),
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && isBillingRequiredError(data)) {
          openBillingRequiredModal(requestId);
          return;
        }
        if (res.status === 403 && isRateAckRequiredError(data) && data.notice) {
          setRateNotice(data.notice);
          setPendingBidRequestId(requestId);
          setRateChangeModalOpen(true);
          return;
        }
        throw new Error(data.message);
      }

      // Check request type for appropriate success message
      const isDirectRequest = directRequests.some(
        (req) => req.id === requestId,
      );
      // isAnnouncement already declared above at line 661

      let successMessage = "✅ Bid submitted successfully!";
      if (isDirectRequest) {
        successMessage =
          "✅ Quote sent to customer! They will be notified to review and accept.";
      } else if (isAnnouncement) {
        successMessage =
          "✅ Quote submitted! Customer will review all quotes and select their preferred shop.";
      }

      alert(successMessage);
      clearQuoteResumeDraft();
      await fetchDashboardData();
      setBidMessages((prev) => ({ ...prev, [requestId]: "" }));
      // Keep selectedSlot for this request so the chosen customer chip stays highlighted for reference
      setProposedStartTimes((prev) => ({ ...prev, [requestId]: "" }));
      setJobDurations((prev) => ({ ...prev, [requestId]: "0" }));
    } catch (err) {
      alert(err.message || "Error submitting bid/quote");
      console.error(err);
    }
  };

  const confirmShopAppointment = async (requestId) => {
    if (!billingReadyForQuotes) {
      openBillingRequiredModal(requestId);
      return;
    }

    if (!(await promptRateChangeIfNeeded(requestId, "confirm"))) {
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}/api/bids/shop-confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ requestId }),
      });

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 403 && isBillingRequiredError(data)) {
          openBillingRequiredModal(requestId);
          return;
        }
        if (response.status === 403 && isRateAckRequiredError(data) && data.notice) {
          setRateNotice(data.notice);
          setPendingConfirmRequestId(requestId);
          setRateChangeModalOpen(true);
          return;
        }
        alert("Failed to confirm: " + (data.message || "Unknown error"));
        return;
      }

      alert("Appointment confirmed! Customer has been notified.");
      await fetchDashboardData();
    } catch (err) {
      alert("Error confirming appointment: " + err.message);
    }
  };

  const confirmIncomingService = async (requestId) => {
    const selected =
      normalizeSelectedSlots(selectedSlot[requestId])[0] || null;
    const time = proposedStartTimes[requestId];
    const price = bidAmounts[requestId];
    const counterDates = counterAvailableDates[requestId];
    let confirmDate = null;
    let formattedTime = null;

    if (counterOffer[requestId]) {
      if (!counterDates || !price) {
        return alert("Select a date and price for your counter offer");
      }
    } else if (!selected || !time || !price) {
      return alert("Select a time and price");
    }

    if (!counterOffer[requestId]) {
      confirmDate = availabilitySlotDateOnly(selected.slot);
      formattedTime = `${time}:00`; // HH:mm:ss
    }

    try {
      const res = await fetch(`${getApiBase()}/api/serviceRequest/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          requestId,
          confirm_date: confirmDate,
          proposed_start_time: formattedTime,
          estimate_price: price,
          autoshop_confirm_date,
          counter_available_date: counterDates,
          shopId: shop.id,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      alert("Service confirmed!");
      await fetchDashboardData();
    } catch (err) {
      console.error("Confirm error", err);
      alert("Failed to confirm service.");
    }
  };

  const cancelQuote = async (requestId, reason) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/serviceRequest/cancel-quote/${requestId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason }),
        },
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to cancel quote");
      }

      return true;
    } catch (err) {
      console.error("Cancel quote error:", err);
      return false;
    }
  };

  const cancelServiceRequest = async (requestId, reason) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/serviceRequest/cancel/${requestId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason: reason,
            shopId: shop.id,
            mechanicId: user.id,
          }),
        },
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to cancel service.");
      }

      return true;
    } catch (err) {
      console.error("Cancel error:", err);
      return false;
    }
  };

  const submitCompletedService = async () => {
    if (
      !completionRequestId ||
      selectedServices.length === 0 ||
      selectedMechanics.length === 0 ||
      selectedServices.some(
        (id) => !serviceCosts[id]?.parts || !serviceCosts[id]?.labor,
      )
    ) {
      return alert(
        "Please complete all fields and enter parts/labor costs for each selected service.",
      );
    }

    /*console.log(
      "Selected Obd Codes: ",
      selectedObdCode,
      "user ID",
      completionUserId,
      "carID",
      completionCarId
    );*/

    try {
      const res = await fetch(`${getApiBase()}/api/serviceRequest/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          requestId: completionRequestId,
          finalPrices: selectedServices.map((serviceId) => ({
            serviceId,
            final_price_parts: Number(serviceCosts[serviceId]?.parts || 0),
            final_price_labor: Number(serviceCosts[serviceId]?.labor || 0),
          })),
          mechanicIds: selectedMechanics,
          notes: completionNotes,
          newMileage: Number(newMileage) || null,
          obdCodes: selectedObdCode, // ← pass codes here
          userId: completionUserId,
          carId: completionCarId,
          isActive: false,
          found_where: "mechanic",
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      alert("Service completed!");
      setShowCompletionModal(false);
      setCompletionRequestId(null);
      setSelectedServices([]);
      setServiceCosts({});
      setSelectedMechanics([]);
      setCompletionNotes("");
      setSelectedObdCode([]);
      await fetchDashboardData();
      await fetchObdCodes(); // optional: refresh view
    } catch (err) {
      console.error("❌ Submit error:", err);
      alert("Failed to submit completed service.");
    }
  };

  const startService = async (requestId) => {
    try {
      const res = await fetch(`${getApiBase()}/api/serviceRequest/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ requestId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      alert("Service moved to In Progress.");
      await fetchDashboardData();
    } catch (err) {
      console.error("Start error", err);
      alert("Failed to start service.");
    }
  };

  const fetchPreselectedServices = async (requestId) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/serviceRequest/requested-services/${requestId}`,
        {
          credentials: "include",
        },
      );
      const data = await res.json();
      const preselectedIds = data.services.map((s) => s.id);
      setSelectedServices(preselectedIds);
    } catch (err) {
      console.error("Failed to load requested services", err);
    }
  };

  // Fetch Platform Ratings for a service request
  const fetchPlatformRatings = async (serviceRequestId, userId, mechanicId) => {
    if (!serviceRequestId || !userId) return;

    try {
      // Fetch service-specific ratings (customer rating, mechanic rating)
      const serviceRes = await fetch(
        `${getApiBase()}/api/platform-ratings/service/${serviceRequestId}`,
        { credentials: "include" },
      );

      if (serviceRes.ok) {
        const serviceData = await serviceRes.json();
        setPlatformRatings((prev) => ({
          ...prev,
          customer_rating: serviceData.customer_rating,
          mechanic_rating: serviceData.mechanic_rating,
        }));
      }

      // Fetch platform rating (Autoshops.com rating) for current user
      const platformRes = await fetch(
        `${getApiBase()}/api/platform-ratings/platform?userId=${userId}${mechanicId ? `&mechanicId=${mechanicId}` : ""}`,
        { credentials: "include" },
      );

      if (platformRes.ok) {
        const platformData = await platformRes.json();
        setPlatformRatings((prev) => ({
          ...prev,
          platform_rating: platformData.platform_rating || null,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch platform ratings:", error);
    }
  };

  // Handle opening rating modal - pre-populate with existing ratings
  const handleOpenRatingModal = (ratingType) => {
    setSelectedRatingType(ratingType);

    // Pre-populate modal with existing rating data
    if (ratingType === "customer" && platformRatings.customer_rating) {
      setRatingModalRating(platformRatings.customer_rating.rating || 0);
      setRatingModalComment(platformRatings.customer_rating.comment || "");
    } else if (ratingType === "platform" && platformRatings.platform_rating) {
      setRatingModalRating(platformRatings.platform_rating.rating || 0);
      setRatingModalComment(platformRatings.platform_rating.comment || "");
    } else {
      setRatingModalRating(0);
      setRatingModalComment("");
    }

    setRatingModalOpen(true);
  };

  // Save rating to backend
  const handleSaveRating = async () => {
    if (!selectedCompleted || !user) return;
    if (ratingModalRating < 1 || ratingModalRating > 5) {
      alert("Please select a rating between 1 and 5 stars");
      return;
    }

    setSavingRating(true);
    try {
      const serviceRequestId = selectedCompleted.request.id;
      const customerId = selectedCompleted.request.user_id;

      const payload = {
        rating_type: selectedRatingType,
        rating: ratingModalRating,
        comment: ratingModalComment.trim() || null,
      };

      if (selectedRatingType === "customer") {
        if (!serviceRequestId || !customerId) {
          throw new Error(
            "Service request ID and customer ID are required for customer ratings",
          );
        }
        payload.service_request_id = serviceRequestId;
        payload.rated_id = customerId;
      } else if (selectedRatingType === "platform") {
        // TASK-564: null/missing role → Car Owner (user_id); shops → mechanic_id
        if (user.role === "Owner" || user.role === "Mechanic") {
          payload.mechanic_id = user.id;
        } else {
          payload.user_id = user.id;
        }
      }

      const response = await fetch(`${getApiBase()}/api/platform-ratings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save rating");
      }

      const savedRating = await response.json();

      // Update local state
      if (selectedRatingType === "customer") {
        setPlatformRatings((prev) => ({
          ...prev,
          customer_rating: savedRating.rating || {
            rating: ratingModalRating,
            comment: ratingModalComment.trim() || null,
          },
        }));
      } else if (selectedRatingType === "platform") {
        setPlatformRatings((prev) => ({
          ...prev,
          platform_rating: savedRating.rating || {
            rating: ratingModalRating,
            comment: ratingModalComment.trim() || null,
          },
        }));
      }

      alert("Rating saved successfully!");
      setRatingModalOpen(false);
    } catch (error) {
      console.error("Failed to save rating:", error);
      alert(error.message || "Failed to save rating");
    } finally {
      setSavingRating(false);
    }
  };

  const openCompletedDetails = async (requestId) => {
    try {
      const res = await fetch(
        `${getApiBase()}/api/serviceRequest/completed-details/${requestId}`,
        {
          credentials: "include",
        },
      );
      const data = await res.json();
      setSelectedCompleted(data);
      setCompletedModalOpen(true);

      // Fetch platform ratings when modal opens
      if (data && data.request && user) {
        const mechanicId = user.id; // Current logged-in mechanic
        fetchPlatformRatings(data.request.id, user.id, mechanicId);
      }
    } catch (err) {
      console.error("Failed to fetch completed service details", err);
    }
  };

  const fetchServicesList = async () => {
    const res = await fetch(`${getApiBase()}/api/services/services`, {
      method: "GET",
      credentials: "include",
    });
    const data = await res.json();
    setAvailableServices(data);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading your dashboard...</Typography>
      </Box>
    );
  }

  if (user?.role === "Mechanic" && user?.is_active === 0) {
    return (
      <>
        <Header />
        <Box
          sx={{
            height: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography variant="h5" color="warning.main">
            Your account is pending approval by the shop owner.
          </Typography>
        </Box>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Box id="mechDash" sx={{ minHeight: "100vh", bgcolor: "#f7f9fc" }}>
        <Box sx={{ maxWidth: "1200px", mx: "auto", p: 4 }}>
          {billingSetupMessage && (
            <Alert
              severity={billingSetupMessage.type}
              sx={{ mb: 3 }}
              onClose={() => setBillingSetupMessage(null)}
            >
              {billingSetupMessage.text}
            </Alert>
          )}
          {/* Shop Status Banners */}
          {shop.status === "Pending" && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#fff3cd",
                border: "2px solid #ffc107",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#856404",
                  fontWeight: 600,
                }}
              >
                ⏳ Your shop is pending admin approval
              </Typography>
              <Typography variant="body2" sx={{ color: "#856404" }}>
                You will be notified once your shop is activated. Some features
                are limited until approval.
              </Typography>
              {/* Progress Indicator */}
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#856404", fontWeight: 600, mb: 1 }}
                >
                  Approval Checklist:
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" sx={{ color: "#856404" }}>
                    {shop.confidence_score >= 50 ? "✅" : "⏳"} Confidence
                    Score: {shop.confidence_score}%{" "}
                    {shop.confidence_score < 50 && "(Need 50%+)"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#856404" }}>
                    {shop.business_license_url ? "✅" : "⏳"} Business License{" "}
                    {!shop.business_license_url && "(Upload in profile)"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#856404" }}>
                    {shop.storefront_photo_url ? "✅" : "⏳"} Storefront Photo{" "}
                    {!shop.storefront_photo_url && "(Upload in profile)"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {shop.status === "Suspended" && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#f8d7da",
                border: "2px solid #dc3545",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#721c24",
                  fontWeight: 600,
                }}
              >
                ⚠️ Your shop has been suspended
              </Typography>
              {shop.rejection_reason && (
                <Typography
                  variant="body2"
                  sx={{ color: "#721c24", fontWeight: 600 }}
                >
                  Reason: {shop.rejection_reason}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: "#721c24" }}>
                Please contact support or fix the issues mentioned above to
                request reactivation.
              </Typography>
            </Box>
          )}

          {/* Shop Switcher - Show if mechanic has multiple shops */}
          {allShops.length > 1 && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: "#f9fafb",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Current Shop
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "#2563eb" }}
                  >
                    {currentShop?.name || currentShop?.shop_name}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowShopSwitcher(!showShopSwitcher)}
                    sx={{ textTransform: "none" }}
                  >
                    {showShopSwitcher ? "Hide" : "Switch Shop"}
                  </Button>
                </Stack>
              </Box>

              {showShopSwitcher && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Select a shop to view its dashboard:
                  </Typography>
                  {allShops.map((shop) => (
                    <Box
                      key={shop.id}
                      onClick={() => handleShopSwitch(shop.id)}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        backgroundColor:
                          currentShop?.id === shop.id ? "#eff6ff" : "white",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {shop.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Role: {shop.role} {shop.is_primary && "• Primary"}
                          </Typography>
                        </Box>
                        {currentShop?.id === shop.id && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Pending Co-Owner Requests */}
          {pendingRequests.length > 0 && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: "#fef3c7",
                borderRadius: "8px",
                border: "1px solid #fbbf24",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                ⚠️ Pending Co-Owner Requests ({pendingRequests.length})
              </Typography>

              <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                {pendingRequests.map((request) => (
                  <Box
                    key={request.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {request.requester_first_name}{" "}
                      {request.requester_last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {request.requester_email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Phone: {request.requester_phone || "Not provided"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Wants to join:{" "}
                      <strong>{request.Autoshops?.shop_name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requested:{" "}
                      {new Date(request.requested_at).toLocaleDateString()}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleApproveRequest(request.id)}
                        sx={{
                          backgroundColor: "#10b981",
                          "&:hover": { backgroundColor: "#059669" },
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleRejectRequest(request.id)}
                        sx={{ borderColor: "#ef4444", color: "#ef4444" }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {shop.status === "Inactive" && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#d1ecf1",
                border: "2px solid #17a2b8",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#0c5460",
                  fontWeight: 600,
                }}
              >
                ℹ️ Your shop is currently inactive
              </Typography>
              <Typography variant="body2" sx={{ color: "#0c5460" }}>
                Your shop is not visible to customers. Click below to request
                reactivation.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#17a2b8",
                  "&:hover": { backgroundColor: "#138496" },
                  alignSelf: "flex-start",
                }}
                onClick={() => {
                  // TODO: Implement request review API call
                  alert("Request review functionality coming soon!");
                }}
              >
                Request Review
              </Button>
            </Box>
          )}

          <Box
            display="flex"
            mb={3}
            sx={{
              mb: 4,
              p: 2,
              borderRadius: 2,
              boxShadow: 3,
              backgroundColor: "#e63926",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 2,
              position: "relative", // Enable absolute positioning for ribbon
              overflow: "visible", // Allow ribbon to extend outside
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#fff",
                alignSelf: "flex-start",
                m: 0,
              }}
            >
              Shop Overview Dashboard
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              {allShops.length >= 1 && (currentShop?.id || shop?.id) && (
                <Button
                  component={RouterLink}
                  to="/calendar"
                  variant="outlined"
                  sx={{
                    color: "#fff",
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#4285f4",
                    textTransform: "none",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "scale(1.03)",
                      backgroundColor: "#1564c0",
                    },
                  }}
                >
                  Calendar
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    shop?.id
                      ? `/mechanic-profile?shopId=${shop.id}`
                      : "/mechanic-profile",
                  )
                }
                sx={{
                  color: "#fff",
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#4285f4",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "scale(1.03)",
                    backgroundColor: "#1564c0",
                  },
                }}
              >
                Service Advisor Profile
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  fetchMessageSummary({ showLoader: true });
                  setMessageCenterOpen(true);
                }}
                sx={{
                  color: "#fff",
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: unreadMessageCount ? "#2e7d32" : "#1565c0",
                  boxShadow: unreadMessageCount
                    ? "0 0 16px rgba(76,175,80,0.6)"
                    : "0 3px 10px rgba(0,0,0,0.2)",
                  animation: unreadMessageCount
                    ? "pulseGlow 1.6s ease-in-out infinite"
                    : "none",
                  "@keyframes pulseGlow": {
                    "0%": { boxShadow: "0 0 0 rgba(76,175,80,0.4)" },
                    "70%": { boxShadow: "0 0 16px rgba(76,175,80,0.9)" },
                    "100%": { boxShadow: "0 0 0 rgba(76,175,80,0.4)" },
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Badge
                    color="error"
                    overlap="circular"
                    badgeContent={unreadMessageCount}
                    invisible={!unreadMessageCount}
                  >
                    <MailOutline fontSize="small" />
                  </Badge>
                  Messages
                </Box>
              </Button>
            </Box>
          </Box>

          <Stack spacing={3}>
            <Paper
              elevation={2}
              sx={{
                mb: 4,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                borderShadow: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#fff",
                  mb: 4,
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#e63926",
                  flexDirection: "row",
                }}
              >
                Shop Overview
              </Typography>

              <Box
                px={2}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  flexWrap: {xs: "wrap", lg: "nowrap"},
                  gap: 4,
                }}
              >
                {/* Left side - Shop Info */}
                <Box
                  sx={{
                    flex: "0 0 auto",
                    maxWidth: "100%",
                  }}
                >
                  {/* Shop Photo */}
                  {shop.photo_url && (
                    <Box
                      component="img"
                      src={shop.photo_url}
                      alt={shop.name}
                      sx={{
                        width: "100%",
                        maxWidth: "400px",
                        height: "auto",
                        borderRadius: 2,
                        mb: 2,
                        boxShadow: 2,
                      }}
                    />
                  )}

                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Shop ID: </strong> {shop.id}
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Name: </strong> {shop.name}
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Address: </strong>
                    {shop.address?.street}, {shop.address?.city},{" "}
                    {shop.address?.state} {shop.address?.zip}
                  </Typography>
                  {(shop.overview_phone || shop.google_phone || shop.phone_number) && (
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>Phone: </strong>{" "}
                      {formatUsPhoneDisplay(
                        shop.overview_phone || shop.google_phone || shop.phone_number,
                      )}
                    </Typography>
                  )}
                </Box>

                {/* Middle - In-Network Checkmark and Active Subscriptions (2-column grid) */}
                <Box
                  sx={{
                    flex: "1 1 auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    minWidth: "200px",
                    maxWidth: "250px",
                  }}
                >
                  {/* Build array of items to display: In-Network checkmark + subscriptions */}
                  {(() => {
                    const displayItems = [];

                    // Add In-Network checkmark if shop is in network
                    if (shop.in_network) {
                      displayItems.push({
                        id: "in_network",
                        type: "checkmark",
                        label: "In-Network",
                      });
                    }

                    // Add active subscriptions
                    if (activeSubscriptions && activeSubscriptions.length > 0) {
                      activeSubscriptions.forEach((sub) => {
                        const display = getSubscriptionDisplay(sub);
                        displayItems.push({
                          id: sub.id,
                          name: sub.name,
                          ...display,
                        });
                      });
                    }

                    if (displayItems.length === 0) return null;

                    // Sort by point value (highest first)
                    displayItems.sort(
                      (a, b) => getPointValue(b) - getPointValue(a),
                    );

                    return (
                      <Grid
                        container
                        spacing={1.5}
                        sx={{ width: "200px", display: "flex" }}
                      >
                        {displayItems.map((item) => (
                          <Grid
                            item
                            xs={6}
                            key={item.id}
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                width: "100%",
                              }}
                            >
                              {item.type === "checkmark" && (
                                <>
                                  <CheckCircle
                                    sx={{
                                      width: "85px",
                                      height: "85px",
                                      color: "#4caf50",
                                      filter:
                                        "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))",
                                    }}
                                  />
                                  <Tooltip
                                    style={{ fontSize: "50" }}
                                    title={
                                      <Box sx={{ p: 0.5, textAlign: "center" }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                        >
                                          In-Network
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ display: "block" }}
                                        >
                                          Verified shop that has been approved
                                          and is part of the Autoshops.com
                                          network.
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        color: "#4caf50",
                                        textAlign: "center",
                                        lineHeight: 1.3,
                                        letterSpacing: "0.3px",
                                        fontFamily:
                                          '"Comic Sans MS", "Marker Felt", "Chalkboard", cursive',
                                        cursor: "help",
                                      }}
                                    >
                                      {item.label}
                                    </Typography>
                                  </Tooltip>
                                </>
                              )}

                              {item.type === "badge" && (
                                <>
                                  <Box
                                    component="img"
                                    src={item.image}
                                    alt={item.label}
                                    sx={{
                                      width: "85px",
                                      height: "85px",
                                      objectFit: "contain",
                                      filter:
                                        "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))",
                                    }}
                                  />
                                  <Tooltip
                                    title={
                                      item.tooltip ? (
                                        <Box
                                          sx={{
                                            p: 0.5,
                                            textAlign: "center",
                                            maxWidth: "200px",
                                          }}
                                        >
                                          {item.tooltip
                                            .split("\n\n")
                                            .map((line, idx) => (
                                              <Typography
                                                key={idx}
                                                variant={
                                                  idx === 0
                                                    ? "body2"
                                                    : "caption"
                                                }
                                                sx={{
                                                  fontWeight:
                                                    idx === 0 ? 600 : 400,
                                                  mb: idx === 0 ? 0.5 : 0,
                                                  display: "block",
                                                }}
                                              >
                                                {line}
                                              </Typography>
                                            ))}
                                        </Box>
                                      ) : (
                                        item.label
                                      )
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        color: item.labelColor || "#d4af37",
                                        textAlign: "center",
                                        lineHeight: 1.3,
                                        letterSpacing: "0.3px",
                                        wordBreak: "break-word",
                                        fontFamily:
                                          '"Comic Sans MS", "Marker Felt", "Chalkboard", cursive',
                                        cursor: item.tooltip
                                          ? "help"
                                          : "default",
                                      }}
                                    >
                                      {item.label}
                                    </Typography>
                                  </Tooltip>
                                </>
                              )}

                              {item.type === "wordart" && (
                                <>
                                  <Box
                                    sx={{
                                      width: "77px",
                                      height: "77px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background:
                                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                      borderRadius: "50%",
                                      boxShadow:
                                        "0 2px 4px rgba(0, 0, 0, 0.15)",
                                      filter:
                                        "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "2.2rem",
                                        fontWeight: 900,
                                        color: "#fff",
                                        lineHeight: 1,
                                        textShadow:
                                          "2px 2px 4px rgba(0, 0, 0, 0.3)",
                                      }}
                                    >
                                      #1
                                    </Typography>
                                  </Box>
                                  <Tooltip
                                    title={
                                      item.tooltip ? (
                                        <Box
                                          sx={{
                                            p: 0.5,
                                            textAlign: "center",
                                            maxWidth: "200px",
                                          }}
                                        >
                                          {item.tooltip
                                            .split("\n\n")
                                            .map((line, idx) => (
                                              <Typography
                                                key={idx}
                                                variant={
                                                  idx === 0
                                                    ? "body2"
                                                    : "caption"
                                                }
                                                sx={{
                                                  fontWeight:
                                                    idx === 0 ? 600 : 400,
                                                  mb: idx === 0 ? 0.5 : 0,
                                                  display: "block",
                                                }}
                                              >
                                                {line}
                                              </Typography>
                                            ))}
                                        </Box>
                                      ) : (
                                        item.label
                                      )
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        color: "#667eea",
                                        textAlign: "center",
                                        lineHeight: 1.3,
                                        letterSpacing: "0.3px",
                                        wordBreak: "break-word",
                                        mt: item.type === "wordart" ? 1 : 0.5,
                                        fontFamily:
                                          '"Comic Sans MS", "Marker Felt", "Chalkboard", cursive',
                                        cursor: item.tooltip
                                          ? "help"
                                          : "default",
                                      }}
                                    >
                                      {item.label}
                                    </Typography>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    );
                  })()}
                </Box>

                {/* Right side - Business Hours */}
                <Box sx={{ flex: "0 0 auto", maxWidth: "100%" }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #ddd",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "1.1rem", fontWeight: 600, mb: 1 }}
                    >
                      Business Hours
                    </Typography>
                    {shop.business_hours ? (
                      <Box>
                        {[
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                          "saturday",
                          "sunday",
                        ].map((day) => {
                          const dayData = shop.business_hours[day];
                          const dayName =
                            day.charAt(0).toUpperCase() + day.slice(1);

                          // Convert 24-hour time to 12-hour AM/PM format
                          const formatTime = (time24) => {
                            if (!time24) return "";
                            const [hours, minutes] = time24.split(":");
                            const hour = parseInt(hours);
                            const ampm = hour >= 12 ? "PM" : "AM";
                            const hour12 =
                              hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            return `${hour12}:${minutes} ${ampm}`;
                          };

                          return (
                            <Typography
                              key={day}
                              sx={{ fontSize: "0.95rem", py: 0.3 }}
                            >
                              <strong>{dayName}:</strong>{" "}
                              {dayData?.closed
                                ? "Closed"
                                : dayData?.open && dayData?.close
                                  ? `${formatTime(dayData.open)} - ${formatTime(dayData.close)}`
                                  : "Not set"}
                            </Typography>
                          );
                        })}
                        {shop.business_hours.source && (
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              color: "#666",
                              mt: 1,
                              fontStyle: "italic",
                            }}
                          >
                            Source:{" "}
                            {shop.business_hours.source === "google_places"
                              ? "Google Places"
                              : shop.business_hours.source}
                            {shop.business_hours.last_updated &&
                              ` • Updated: ${shop.business_hours.last_updated}`}
                          </Typography>
                        )}
                        {user?.role === "Owner" && (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            sx={{ mt: 2, textTransform: "none" }}
                            onClick={() =>
                              alert("Edit hours functionality coming soon!")
                            }
                          >
                            Edit Business Hours
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          sx={{ fontSize: "0.95rem", color: "#666", mb: 2 }}
                        >
                          No business hours set for your shop.
                        </Typography>
                        {user?.role === "Owner" && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            fullWidth
                            sx={{ textTransform: "none" }}
                            onClick={() =>
                              alert("Add hours functionality coming soon!")
                            }
                          >
                            Add Business Hours
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box px={2}>
                {/* TODO: Uncomment when confidence score functionality is 100% complete
                {shop.id && (
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <ConfidenceScoreSection 
                      shopId={shop.id} 
                      shopName={shop.name}
                      shopWebsite={shop.website || shop.site}
                      isCompact={true}
                    />
                  </Box>
                )}
                */}

                <Divider sx={{ my: 2 }} />

                {/* Overall rating line */}
                <Box
                  mb={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#fbf4f4ff",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "center",
                    }}
                    gap={2}
                    mb={1}
                  >
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                      Overall Rating:
                    </Typography>
                    <Rating
                      value={shopReviews?.avg_rating || 0}
                      precision={0.1}
                      readOnly
                    />
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>{shopReviews?.avg_rating ?? "—"}</strong> (
                      {shopReviews?.review_count || 0}{" "}
                      {shopReviews?.review_count === 1 ? "review" : "reviews"})
                    </Typography>
                  </Box>
                </Box>

                {/* Reviews accordion */}
                <Accordion sx={{ boxShadow: 1, mx: 2, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      Customer Reviews
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {!shopReviews || (shopReviews.review_count ?? 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No reviews yet.
                      </Typography>
                    ) : (
                      <>
                        <List dense disablePadding>
                          {shopReviews.reviews.map((r) => (
                            <ReviewListItem
                              key={r.id}
                              review={r}
                              user={user}
                              onOpenConversation={openReviewConversation}
                              fetchComments={fetchReviewComments}
                            />
                          ))}
                        </List>

                        {/* See more reviews link */}
                        {shop?.place_id && (
                          <Box
                            sx={{
                              textAlign: "center",
                              mt: 2,
                              pt: 2,
                              borderTop: "1px solid #e0e0e0",
                            }}
                          >
                            <Button
                              variant="text"
                              href={`https://search.google.com/local/reviews?placeid=${shop.place_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: "#4285F4",
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": {
                                  backgroundColor: "rgba(66, 133, 244, 0.1)",
                                },
                              }}
                            >
                              See more reviews on Google →
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>

            <ShopTeamRoster
              shopId={shop?.id}
              shopStatus={shop?.status}
              currentUserId={user?.id}
              isOwner={
                (allShops.find((row) => row.id === shop?.id)?.role || user?.role) ===
                "Owner"
              }
            />

            {/* Unified Service Requests Section */}
            <Paper
              elevation={2}
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 2,
                borderShadow: 3,
              }}
            >
              {/* Main Service Requests Banner - RED, Collapsible */}
              <Accordion
                id="service-requests-section"
                expanded={serviceRequestsPanelExpanded}
                onChange={(_, expanded) => setServiceRequestsPanelExpanded(expanded)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  sx={{
                    backgroundColor: "#e63926", // Primary red
                    color: "#fff",
                    minHeight: 72, // Match other main category banners
                    padding: "16px 24px", // Match padding: p: 2 = 16px vertical, 24px horizontal
                    borderRadius: 2, // Match other main category banners (8px)
                    borderBottom: "2px solid #fff", // White border to separate from sub-headers
                    "& .MuiAccordionSummary-content": {
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "16px 0", // Add vertical margin
                    },
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    📋 Service Requests
                  </Typography>

                  {/* Discrete Counter Badge - positioned LEFT of caret */}
                  <Badge
                    badgeContent={totalAttentionCount}
                    sx={{
                      "& .MuiBadge-badge": {
                        position: "relative",
                        transform: "none",
                        backgroundColor: "#fff",
                        color: "#e63926",
                        fontWeight: "bold",
                        fontSize: "0.875rem", // Smaller font
                        minWidth: "24px", // Smaller badge
                        height: "24px",
                        borderRadius: "12px",
                        marginRight: "16px", // Space before caret
                      },
                    }}
                  >
                    <span></span> {/* Empty span for badge positioning */}
                  </Badge>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, backgroundColor: "#f5f5f5" }}>
                  {/* Direct Requests Sub-Header - DARKER RED */}
                  <Accordion
                    id="direct-requests-section"
                    expanded={directRequestsPanelExpanded}
                    onChange={(_, expanded) => setDirectRequestsPanelExpanded(expanded)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                      sx={{
                        backgroundColor: "#d32f2f", // Darker red to differentiate from main banner
                        color: "#fff",
                        minHeight: 56,
                        "& .MuiAccordionSummary-content": {
                          justifyContent: "space-between",
                          alignItems: "center",
                        },
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="600">
                        📍 Direct Requests
                      </Typography>

                      {/* Counter badge */}
                      {directAttentionCount > 0 && (
                        <Box
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white
                            color: "#fff",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            mr: 2,
                          }}
                        >
                          {directAttentionCount}
                        </Box>
                      )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2, backgroundColor: "#fff" }}>
                      {allServiceRequests.filter(
                        (req) => req.request_display_type === "Direct",
                      ).length > 0 ? (
                        <Box>
                          {allServiceRequests
                            .filter(
                              (req) => req.request_display_type === "Direct",
                            )
                            .map((req) => {
                              const isExpanded =
                                expandedServiceRequests[req.id] || false;

                              return (
                                <ServiceRequestCard
                                  key={req.id}
                                  request={req}
                                  shop={shop}
                                  hasExtendedServiceHistory={
                                    !!hasExtendedServiceHistory
                                  }
                                  isExtendedHistoryActive={
                                    isExtendedHistoryActive
                                  }
                                  isExtendedHistoryPlanAvailable={
                                    isExtendedHistoryPlanAvailableBool
                                  }
                                  userRole={user?.role}
                                  needsAttention={needsAttention(req)}
                                  parentColor="#d32f2f"
                                  onOpenConversation={
                                    handleOpenConversationModal
                                  }
                                  onExtendedHistoryClick={(request) => {
                                    setSelectedServiceRequest(request);
                                    setServiceHistoryModalOpen(true);
                                  }}
                                  onCancelQuote={(requestId) => {
                                    setSelectedRequestId(requestId);
                                    setCancelQuoteDialogOpen(true);
                                  }}
                                  onRefresh={fetchDashboardData}
                                  onSubmitBid={submitBid}
                                  onShopConfirm={confirmShopAppointment}
                                  billingReadyForQuotes={billingReadyForQuotes}
                                  onBillingRequired={openBillingRequiredModal}
                                  onPriceChange={handlePriceChange}
                                  // State props
                                  bidAmounts={bidAmounts}
                                  setBidAmounts={setBidAmounts}
                                  selectedSlot={selectedSlot}
                                  setSelectedSlot={setSelectedSlot}
                                  proposedHours={proposedHours}
                                  setProposedHours={setProposedHours}
                                  proposedMinutes={proposedMinutes}
                                  setProposedMinutes={setProposedMinutes}
                                  proposedAmpm={proposedAmpm}
                                  setProposedAmpm={setProposedAmpm}
                                  proposedStartTimes={proposedStartTimes}
                                  setProposedStartTimes={setProposedStartTimes}
                                  jobDurations={jobDurations}
                                  setJobDurations={setJobDurations}
                                  counterOffer={counterOffer}
                                  setCounterOffer={setCounterOffer}
                                  counterAvailableDates={counterAvailableDates}
                                  setCounterAvailableDates={
                                    setCounterAvailableDates
                                  }
                                  selectedCounterDate={selectedCounterDate}
                                  setSelectedCounterDate={
                                    setSelectedCounterDate
                                  }
                                  altTimeHour={altTimeHour}
                                  setAltTimeHour={setAltTimeHour}
                                  altTimeMinute={altTimeMinute}
                                  setAltTimeMinute={setAltTimeMinute}
                                  altTimeAmpm={altTimeAmpm}
                                  setAltTimeAmpm={setAltTimeAmpm}
                                  // Announcement-specific state (not used for Direct, but needed for component)
                                  shopTimeSlots={shopTimeSlots}
                                  setShopTimeSlots={setShopTimeSlots}
                                  dropoffDates={dropoffDates}
                                  setDropoffDates={setDropoffDates}
                                  // Expanded state
                                  isExpanded={isExpanded}
                                  onToggleExpanded={() => {
                                    setExpandedServiceRequests((prev) => ({
                                      ...prev,
                                      [req.id]: !prev[req.id],
                                    }));
                                  }}
                                />
                              );
                            })}
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            textAlign: "center",
                            py: 3,
                          }}
                          color="text.secondary"
                        >
                          No direct service requests.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Broadcast Requests Sub-Header - BLUE */}
                  {hasAnnouncementsEnabled && (
                    <Accordion
                      id="broadcast-requests-section"
                      expanded={broadcastRequestsPanelExpanded}
                      onChange={(_, expanded) => setBroadcastRequestsPanelExpanded(expanded)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                        sx={{
                          backgroundColor: "#4285f4", // Blue
                          color: "#fff",
                          minHeight: 56,
                          "& .MuiAccordionSummary-content": {
                            justifyContent: "space-between",
                            alignItems: "center",
                          },
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="600">
                          📡 Broadcast Requests
                        </Typography>

                        {/* Counter badge */}
                        {broadcastAttentionCount > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white
                              color: "#fff",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "0.75rem",
                              mr: 2,
                            }}
                          >
                            {broadcastAttentionCount}
                          </Box>
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2, backgroundColor: "#fff" }}>
                        {allServiceRequests.filter(
                          (req) => req.request_display_type === "Broadcast",
                        ).length > 0 ? (
                          <Box>
                            {allServiceRequests
                              .filter(
                                (req) =>
                                  req.request_display_type === "Broadcast",
                              )
                              .map((req) => {
                                const isExpanded =
                                  expandedServiceRequests[req.id] || false;

                                return (
                                  <ServiceRequestCard
                                    key={req.id}
                                    request={req}
                                    shop={shop}
                                    hasExtendedServiceHistory={
                                      !!hasExtendedServiceHistory
                                    }
                                    isExtendedHistoryActive={
                                      isExtendedHistoryActive
                                    }
                                    isExtendedHistoryPlanAvailable={
                                      isExtendedHistoryPlanAvailableBool
                                    }
                                    userRole={user?.role}
                                    needsAttention={needsAttention(req)}
                                    parentColor="#4285f4"
                                    onOpenConversation={
                                      handleOpenConversationModal
                                    }
                                    onExtendedHistoryClick={(request) => {
                                      setSelectedServiceRequest(request);
                                      setServiceHistoryModalOpen(true);
                                    }}
                                    onCancelQuote={(requestId) => {
                                      setSelectedRequestId(requestId);
                                      setCancelQuoteDialogOpen(true);
                                    }}
                                    onRefresh={fetchDashboardData}
                                    onSubmitBid={submitBid}
                                    onShopConfirm={confirmShopAppointment}
                                    billingReadyForQuotes={billingReadyForQuotes}
                                    onBillingRequired={openBillingRequiredModal}
                                    onPriceChange={handlePriceChange}
                                    // State props
                                    bidAmounts={bidAmounts}
                                    setBidAmounts={setBidAmounts}
                                    selectedSlot={selectedSlot}
                                    setSelectedSlot={setSelectedSlot}
                                    proposedHours={proposedHours}
                                    setProposedHours={setProposedHours}
                                    proposedMinutes={proposedMinutes}
                                    setProposedMinutes={setProposedMinutes}
                                    proposedAmpm={proposedAmpm}
                                    setProposedAmpm={setProposedAmpm}
                                    proposedStartTimes={proposedStartTimes}
                                    setProposedStartTimes={
                                      setProposedStartTimes
                                    }
                                    jobDurations={jobDurations}
                                    setJobDurations={setJobDurations}
                                    counterOffer={counterOffer}
                                    setCounterOffer={setCounterOffer}
                                    counterAvailableDates={
                                      counterAvailableDates
                                    }
                                    setCounterAvailableDates={
                                      setCounterAvailableDates
                                    }
                                    selectedCounterDate={selectedCounterDate}
                                    setSelectedCounterDate={
                                      setSelectedCounterDate
                                    }
                                    altTimeHour={altTimeHour}
                                    setAltTimeHour={setAltTimeHour}
                                    altTimeMinute={altTimeMinute}
                                    setAltTimeMinute={setAltTimeMinute}
                                    altTimeAmpm={altTimeAmpm}
                                    setAltTimeAmpm={setAltTimeAmpm}
                                    // Announcement-specific state
                                    shopTimeSlots={shopTimeSlots}
                                    setShopTimeSlots={setShopTimeSlots}
                                    dropoffDates={dropoffDates}
                                    setDropoffDates={setDropoffDates}
                                    // Expanded state
                                    isExpanded={isExpanded}
                                    onToggleExpanded={() => {
                                      setExpandedServiceRequests((prev) => ({
                                        ...prev,
                                        [req.id]: !prev[req.id],
                                      }));
                                    }}
                                  />
                                );
                              })}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "1.1rem",
                              textAlign: "center",
                              py: 3,
                            }}
                            color="text.secondary"
                          >
                            No broadcast service requests.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </AccordionDetails>
              </Accordion>
            </Paper>
            {/* End of Service Requests Section - Now using ServiceRequestCard for both Direct and Broadcast */}

            <Paper
              elevation={2}
              sx={{
                mb: 4,
                p: 3,
                borderRadius: 2,
                borderShadow: 3,
              }}
            >
              <Accordion
                defaultExpanded={false}
                sx={{
                  borderRadius: "8px",
                  boxShadow: "none",
                  backgroundColor: "#fff",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  sx={{
                    backgroundColor: "#e63926",
                    color: "#fff",
                    minHeight: 72, // Match other main banners
                    padding: "16px 24px", // Match other banners
                    borderRadius: "8px 8px 0 0",
                    borderBottom: "2px solid #fff",
                    "& .MuiAccordionSummary-content": {
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "16px 0", // Add vertical margin
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <BuildIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Services In Progress
                    </Typography>
                  </Box>

                  {/* Discrete Counter Badge - positioned LEFT of caret */}
                  <Badge
                    badgeContent={inProgressServices.length}
                    sx={{
                      "& .MuiBadge-badge": {
                        position: "relative",
                        transform: "none",
                        backgroundColor: "#fff",
                        color: "#e63926",
                        fontWeight: "bold",
                        fontSize: "0.875rem", // Smaller font
                        minWidth: "24px", // Smaller badge
                        height: "24px",
                        borderRadius: "12px",
                        marginRight: "16px", // Space before caret
                      },
                    }}
                  >
                    <span></span> {/* Empty span for badge positioning */}
                  </Badge>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 3 }}>
                  {inProgressServices.length > 0 ? (
                    <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                      {inProgressServices.map((req) => {
                        const pastDue = isPastDueAccepted(req);
                        const isCancelDisabled = !canCancelService(req);

                        return (
                          <Accordion
                            key={req.id}
                            sx={{
                              mb: 1,
                              boxShadow: 1,
                              ...(pastDue && {
                                border: "2px solid #d32f2f",
                                backgroundColor: "#fff8f8",
                              }),
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  width: "100%",
                                  pr: 2,
                                }}
                              >
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "1.1rem",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Request #{req.id} - {req.user}
                                      {req.car && ` - ${req.car}`}
                                    </Typography>

                                    {pastDue && (
                                      <Chip
                                        label="PAST DUE"
                                        size="small"
                                        sx={{
                                          backgroundColor: "#d32f2f",
                                          color: "#fff",
                                          fontWeight: "bold",
                                          fontSize: "0.7rem",
                                          height: 22,
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {req ? dateTimeText(req) : "Loading..."};
                                  </Typography>
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {pastDue && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  <Typography fontWeight="bold">
                                    Appointment Missed
                                  </Typography>
                                  <Typography variant="body2">
                                    This service request is past its scheduled
                                    time. Please contact the customer to follow
                                    up and reschedule if needed. You will be
                                    able to cancel this service after 2 days.
                                  </Typography>
                                </Alert>
                              )}
                              <Grid container spacing={3}>
                                {/* Left Column - Service Request Details */}
                                <Grid item xs={12} md={8}>
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{ mb: 2, fontWeight: 600 }}
                                    >
                                      Service Request Details
                                    </Typography>
                                    {req.services &&
                                      req.services.length > 0 && (
                                        <Typography sx={{ mb: 1.5 }}>
                                          <strong>Services:</strong>{" "}
                                          {req.services.join(", ")}
                                        </Typography>
                                      )}
                                    {req.description && (
                                      <Typography sx={{ mb: 1.5 }}>
                                        <strong>Customer Note:</strong>{" "}
                                        {req.description}
                                      </Typography>
                                    )}
                                    {req.estimated_price && (
                                      <Typography sx={{ mb: 1.5 }}>
                                        <strong>Estimated Price:</strong> $
                                        {req.estimated_price}
                                      </Typography>
                                    )}
                                    <Typography sx={{ mb: 1.5 }}>
                                      <strong>Status:</strong> {req.status}
                                    </Typography>
                                    {req.customer_selected_slot_details ? (
                                      // Show the actual selected time
                                      <Typography sx={{ mb: 1.5 }}>
                                        <strong>Appointment:</strong>{" "}
                                        {format(
                                          parseISO(
                                            req.customer_selected_slot_details
                                              .available_date,
                                          ),
                                          "EEE, MMM d, yyyy",
                                        )}{" "}
                                        at{" "}
                                        {(() => {
                                          const formatTime = (time24) => {
                                            const [hourStr, minuteStr] =
                                              time24.split(":");
                                            const hour24 = parseInt(hourStr);
                                            const hour12 =
                                              hour24 === 0
                                                ? 12
                                                : hour24 > 12
                                                  ? hour24 - 12
                                                  : hour24;
                                            const ampm =
                                              hour24 < 12 ? "AM" : "PM";
                                            return `${hour12}:${minuteStr} ${ampm}`;
                                          };
                                          return `${formatTime(req.customer_selected_slot_details.start_time)} - ${formatTime(req.customer_selected_slot_details.end_time)}`;
                                        })()}
                                      </Typography>
                                    ) : (
                                      <>
                                        {req.confirm_date && (
                                          <Typography sx={{ mb: 1.5 }}>
                                            <strong>Confirmed Date:</strong>{" "}
                                            {req.confirm_date}
                                          </Typography>
                                        )}
                                        {req.proposed_start_time && (
                                          <Typography sx={{ mb: 1.5 }}>
                                            <strong>Start Time:</strong>{" "}
                                            {(() => {
                                              try {
                                                const timeStr =
                                                  req.proposed_start_time
                                                    .toString()
                                                    .split(".")[0];
                                                const parsed = parse(
                                                  timeStr,
                                                  "HH:mm:ss",
                                                  new Date(),
                                                );
                                                return format(parsed, "h:mm a");
                                              } catch (err) {
                                                return req.proposed_start_time;
                                              }
                                            })()}
                                          </Typography>
                                        )}
                                        {req.is_dropoff && (
                                          <Typography
                                            sx={{ mb: 1.5 }}
                                            color="primary"
                                          >
                                            🚗 Drop-off Service (Customer picks
                                            up at close or when notified)
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </Grid>

                                {/* Right Column - Action Buttons */}
                                <Grid
                                  item
                                  xs={12}
                                  md={4}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1.5,
                                      alignItems: "flex-end",
                                    }}
                                  >
                                    {req.status === "Accepted" ||
                                    req.status === "Confirmed" ? (
                                      <Button
                                        size="medium"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => startService(req.id)}
                                      >
                                        Start Service
                                      </Button>
                                    ) : req.status === "Pending" ? (
                                      req.bid_status === "customer_accepted" ? (
                                        <Box>
                                          <Alert
                                            severity="success"
                                            sx={{ mb: 2 }}
                                          >
                                            <Typography
                                              variant="body1"
                                              fontWeight="bold"
                                            >
                                              🎉 Customer Accepted Your Quote!
                                            </Typography>
                                            <Typography variant="body2">
                                              Confirm the appointment to
                                              proceed.
                                            </Typography>
                                          </Alert>
                                          <Button
                                            variant="contained"
                                            size="large"
                                            color="success"
                                            onClick={() => confirmShopAppointment(req.id)}
                                          >
                                            ✅ Confirm Appointment
                                          </Button>
                                        </Box>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          ⏳ Waiting on customer to accept your
                                          quote
                                        </Typography>
                                      )
                                    ) : (
                                      <Stack spacing={1} direction="column">
                                        <Button
                                          size="medium"
                                          variant="contained"
                                          color="primary"
                                          onClick={() => {
                                            setCompletionRequestId(req.id);
                                            fetchServicesList();
                                            fetchPreselectedServices(req.id);
                                            setShowCompletionModal(true);
                                            setCompletionCarId(req.car_id);
                                            setCompletionUserId(req.user_id);
                                          }}
                                        >
                                          Mark Complete
                                        </Button>
                                        {!canCancelService(req) && pastDue ? (
                                          <Tooltip
                                            title={
                                              <Typography fontSize={15}>
                                                "Cancellation allowed 2 days
                                                after appointment date"
                                              </Typography>
                                            }
                                          >
                                            <span>
                                              <Button
                                                size="medium"
                                                variant="outlined"
                                                color="error"
                                                disabled
                                                startIcon={<Close />}
                                              >
                                                Cancel
                                              </Button>
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Button
                                            size="medium"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Close />}
                                            onClick={() => {
                                              setCancelRequestId(req.id);
                                              setCancelDialogOpen(true);
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        )}
                                      </Stack>
                                    )}
                                    <Button
                                      size="medium"
                                      variant="contained"
                                      onClick={() =>
                                        handleOpenConversationModal(req)
                                      }
                                      className={
                                        hasUnreadMessages(req.id)
                                          ? "pulse-button"
                                          : ""
                                      }
                                      sx={{
                                        bgcolor: hasUnreadMessages(req.id)
                                          ? "#4CAF50"
                                          : "#e63926",
                                        color: "#fff",
                                        textTransform: "none",
                                        "&:hover": {
                                          bgcolor: hasUnreadMessages(req.id)
                                            ? "#43a047"
                                            : "#c62f1e",
                                        },
                                      }}
                                    >
                                      {hasUnreadMessages(req.id)
                                        ? "New Message"
                                        : "Message"}
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      No services in progress.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper
              elevation={2}
              sx={{
                mb: 4,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                borderShadow: 3,
              }}
            >
              <Accordion
                defaultExpanded={false}
                sx={{
                  borderRadius: "8px",
                  boxShadow: "none",
                  backgroundColor: "#fff",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  sx={{
                    backgroundColor: "#e63926",
                    color: "#fff",
                    minHeight: 72, // Match other main banners
                    padding: "0 24px",
                    borderRadius: "8px 8px 0 0",
                    borderBottom: "2px solid #fff",
                    "& .MuiAccordionSummary-content": {
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "16px 0",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Completed Services
                    </Typography>

                    {(() => {
                      // Check if extended service history subscription exists and is active
                      const extendedHistorySub = activeSubscriptions.find(
                        (sub) =>
                          sub.name === "addon_service_history" ||
                          sub.name?.includes("service_history") ||
                          sub.name?.includes("extended"),
                      );
                      const isFeatureActive =
                        extendedHistorySub?.is_active !== 0;
                      return hasExtendedServiceHistory && isFeatureActive;
                    })() ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexDirection: { xs: "column", md: "row" },
                          position: "relative",
                          ml: "auto",
                          mr: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            background:
                              "linear-gradient(135deg, rgba(255,215,0,0.25) 0%, rgba(255,255,255,0.1) 100%)",
                            color: "#fff",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1.2,
                            borderRadius: "0 0 8px 8px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "2px",
                              background:
                                "linear-gradient(90deg, rgba(255,215,0,0.6) 0%, rgba(255,255,255,0.4) 50%, rgba(255,215,0,0.6) 100%)",
                            },
                          }}
                        >
                          <span style={{ fontSize: "0.9rem" }}>✨</span>
                          Extended Service History
                        </Box>
                        <FormControl
                          size="small"
                          sx={{
                            minWidth: "180px",
                            "& .MuiInputBase-input": {
                              color: "#fff",
                              fontSize: "1rem",
                            },
                            "& .MuiInputLabel-root": {
                              color: "#fff",
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#fff",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#fff",
                              },
                            },
                            "& .MuiSvgIcon-root": {
                              color: "#fff",
                            },
                          }}
                        >
                          <InputLabel sx={{ color: "#fff" }}>
                            Filter History
                          </InputLabel>
                          <Select
                            value={serviceFilter}
                            onChange={(e) => setServiceFilter(e.target.value)}
                            label="Filter History"
                            sx={{ color: "#fff" }}
                          >
                            <MenuItem value="all">All Time</MenuItem>
                            <MenuItem value="30days">Last 30 Days</MenuItem>
                            <MenuItem value="90days">Last 90 Days</MenuItem>
                            <MenuItem value="thisMonth">This Month</MenuItem>
                            <MenuItem value="thisYear">This Year</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    ) : (
                      // Only show "Unlock Extended History" button for Owners if the plan is available
                      user?.role === "Owner" &&
                      isExtendedHistoryPlanAvailable && (
                        <Button
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExtendedHistoryModalOpen(true);
                          }}
                          startIcon={<Bolt />}
                          sx={{
                            background:
                              "linear-gradient(90deg, #fff 0%, #ffd6d2 100%)",
                            color: "#c6281d",
                            fontWeight: 600,
                            textTransform: "none",
                            border: "2px solid #fff",
                            boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                            ml: "auto",
                            mr: 2,
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, #ffe5e3 0%, #ffbdb5 100%)",
                            },
                          }}
                        >
                          Unlock Extended History
                        </Button>
                      )
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 3 }}>
                  <Box px={2}>
                    {completedServices.length > 0 ? (
                      <List dense sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                        {completedServices.map((service, idx) => (
                          <ListItem
                            key={idx}
                            divider
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              size: { xs: "large", sm: "small" },
                              py: 2,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "1.1rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {`Request #${service.id} - ${service.user}`}
                                  </Typography>
                                  {service.car && (
                                    <Typography
                                      sx={{
                                        fontSize: "1rem",
                                        color: "text.secondary",
                                      }}
                                    >
                                      {service.car}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <Typography sx={{ fontSize: "1rem" }}>
                                    {service.activity_date
                                      ? `Completed on ${new Date(service.activity_date).toLocaleDateString()}`
                                      : "Completion date unavailable"}
                                  </Typography>
                                  {!service.isOwnShop && service.shopName && (
                                    <Box
                                      sx={{
                                        bgcolor: "rgba(66, 133, 244, 0.12)",
                                        color: "#1a4fa3",
                                        borderRadius: 1.5,
                                        px: 1.25,
                                        py: 0.4,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        fontWeight: 600,
                                        fontSize: "0.9rem",
                                      }}
                                    >
                                      {`Performed by ${service.shopName}`}
                                    </Box>
                                  )}
                                </Box>
                              }
                              sx={{
                                py: 0.5,
                                width: { xs: "100%", sm: "auto" },
                              }}
                            />
                            <Box
                              gap={1}
                              pr={1}
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "column" },
                                alignItems: { xs: "stretch", sm: "flex-end" },
                                justifyContent: "space-between",
                                width: { xs: "100%", sm: "auto" },
                              }}
                            >
                              <Chip
                                label={service.status}
                                size="small"
                                color={
                                  service.status === "Completed"
                                    ? "success"
                                    : service.status === "Cancelled"
                                      ? "error"
                                      : "warning"
                                }
                              />
                              <Button
                                size="small"
                                onClick={() => openCompletedDetails(service.id)}
                              >
                                See More
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  handleOpenConversationModal({
                                    id: service.id,
                                    user: service.user,
                                  })
                                }
                                className={
                                  hasUnreadMessages(service.id)
                                    ? "pulse-button"
                                    : ""
                                }
                                sx={{
                                  bgcolor: hasUnreadMessages(service.id)
                                    ? "#4CAF50"
                                    : "#e63926",
                                  color: "#fff",
                                  textTransform: "none",
                                  "&:hover": {
                                    bgcolor: hasUnreadMessages(service.id)
                                      ? "#43a047"
                                      : "#c62f1e",
                                  },
                                }}
                              >
                                {hasUnreadMessages(service.id)
                                  ? "New Message"
                                  : "Message"}
                              </Button>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography
                        color="textSecondary"
                        sx={{ fontSize: "1.1.rem", py: 0.5 }}
                      >
                        No Services Completed
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            {/* Bidding Board - Pending Services - Only show if bidding services are active */}
            {hasBiddingEnabled && (
              <Paper
                elevation={2}
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  borderShadow: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#e63926",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  gutterBottom
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#fff",
                      mb: { xs: 3, md: 0 },
                    }}
                    gutterBottom
                  >
                    Bidding Board - Pending Services
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                    }}
                  >
                    <FormControl
                      size="small"
                      sx={{
                        width: "120px",
                        mr: 3,
                        "& .MuiInputBase-input": {
                          color: "#fff",
                          fontSize: "1.1rem",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#fff",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#fff",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#fff",
                          },
                        },
                      }}
                    >
                      <InputLabel sx={{ color: "#fff" }}>
                        Radius (miles)
                      </InputLabel>
                      <Select
                        value={radius}
                        onChange={(e) => setRadius(Number(e.target.value))}
                        label="Radius (miles)"
                        sx={{ color: "#fff", height: "100%" }}
                      >
                        {[5, 10, 15, 25].map((option) => (
                          <MenuItem key={option} value={option}>
                            {option} miles
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      onClick={fetchDashboardData}
                      disabled={loading}
                    >
                      Apply Radius
                    </Button>
                  </Box>
                </Box>

                {biddingRequests.length > 0 ? (
                  <List
                    sx={{
                      maxHeight: "80vh",
                      overflowY: "auto",
                    }}
                  >
                    {biddingRequests.map((req, idx) => {
                      const now = new Date();
                      const hasRecentBid = req.bids?.some((bid) => {
                        if (bid.shop_name === shop.name) {
                          const timeDiff =
                            now.getTime() - new Date(bid.bid_time).getTime();
                          // If negative (bid appears in future due to timezone issue), treat as old bid
                          if (timeDiff < 0) return false;
                          return timeDiff < 60 * 60 * 1000; // 1 hour cooldown
                        }
                        return false;
                      });

                      return (
                        <Box
                          key={idx}
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: 4,
                            p: 2,
                            mb: 2,
                            backgroundColor: "#fff",
                            borderColor: "000",
                          }}
                        >
                          <ListItem alignItems="flex-start" divider>
                            <ListItemText
                              primary={`Request #${req.id} from ${req.user}`}
                              primaryTypographyProps={{
                                sx: {
                                  fontSize: "1.1rem",
                                  fontWeight: "bold",
                                  mb: 2,
                                },
                              }}
                              secondary={
                                <>
                                  <Typography
                                    sx={{ fontSize: "1.1rem", mb: 1 }}
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <strong>Car: </strong> {req.car}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "1.1rem", mb: 1 }}
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <strong> Services:</strong> {req.services}
                                  </Typography>

                                  <Typography
                                    sx={{ fontSize: "1.1rem", mb: 1 }}
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <strong>Note for Customer:</strong>{" "}
                                    {req.description}
                                  </Typography>
                                  <TextField
                                    label="Message"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={bidMessages[req.id] || ""}
                                    onChange={(e) =>
                                      setBidMessages((prev) => ({
                                        ...prev,
                                        [req.id]: e.target.value,
                                      }))
                                    }
                                    sx={{ fontSize: "1.1rem", mt: 1, mb: 2 }}
                                  />

                                  <Typography
                                    sx={{ fontSize: "1.1rem", mb: 1, mt: 2 }}
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <strong>Available Dates & Times:</strong>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="primary"
                                    sx={{
                                      display: "block",
                                      mb: 1,
                                      fontStyle: "italic",
                                      fontWeight: 500,
                                    }}
                                  >
                                    👆 Click a date below to select it, then
                                    choose your proposed start time and
                                    estimated duration in hours
                                  </Typography>

                                  {Array.isArray(req.availableDates) &&
                                  req.availableDates.length > 0 ? (
                                    <>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        sx={{ mb: 2 }}
                                      >
                                        {req.availableDates.map((slot, i) => {
                                          // TASK-889: same Direct drop-off chips as website /
                                          // car-owner list — date-only is_dropoff rows are
                                          // selectable (not "Incomplete time slot").
                                          const dateOnly = (
                                            slot?.date ||
                                            slot?.available_date ||
                                            ""
                                          )
                                            .toString()
                                            .split("T")[0];
                                          // Only true drop-off rows (website + Voice Direct).
                                          // Timed preferred chips stay on the appointment path.
                                          const isDropoffChip =
                                            !!dateOnly &&
                                            slot.is_dropoff === true;

                                          if (isDropoffChip) {
                                            const isSelected =
                                              isAvailabilityChipSelected(
                                                selectedSlot[req.id],
                                                i,
                                              );
                                            let dropLabel = `Drop-off: ${format(
                                              parseISO(dateOnly),
                                              "EEE, MMM d, yyyy",
                                            )}`;
                                            // Defaults from Voice intake: drop by (open+1h) · pickup after (close−1h)
                                            if (
                                              slot.start_time &&
                                              slot.end_time
                                            ) {
                                              try {
                                                const dropBy = format(
                                                  parseISO(
                                                    `${dateOnly}T${slot.start_time}`,
                                                  ),
                                                  "h:mm a",
                                                );
                                                const pickupAfter = format(
                                                  parseISO(
                                                    `${dateOnly}T${slot.end_time}`,
                                                  ),
                                                  "h:mm a",
                                                );
                                                dropLabel = `Drop-off: ${format(
                                                  parseISO(dateOnly),
                                                  "EEE, MMM d, yyyy",
                                                )} (drop by ${dropBy} · pickup after ${pickupAfter})`;
                                              } catch {
                                                /* keep date-only label */
                                              }
                                            }
                                            return (
                                              <Chip
                                                key={i}
                                                label={dropLabel}
                                                clickable
                                                color={
                                                  isSelected
                                                    ? "primary"
                                                    : "default"
                                                }
                                                variant={
                                                  isSelected
                                                    ? "filled"
                                                    : "outlined"
                                                }
                                                onClick={() => {
                                                  setSelectedSlot((prev) => {
                                                    const nextList =
                                                      toggleAvailabilityChip(
                                                        prev[req.id],
                                                        i,
                                                        {
                                                          ...slot,
                                                          date: dateOnly,
                                                          is_dropoff: true,
                                                        },
                                                      );
                                                    const next = { ...prev };
                                                    if (nextList.length === 0) {
                                                      delete next[req.id];
                                                    } else {
                                                      next[req.id] = nextList;
                                                    }
                                                    return next;
                                                  });
                                                }}
                                                sx={{
                                                  mr: 0.5,
                                                  mb: 0.5,
                                                  borderColor: "#ff9800",
                                                  backgroundColor: isSelected
                                                    ? undefined
                                                    : "#fff3e0",
                                                }}
                                              />
                                            );
                                          }

                                          if (
                                            !slot ||
                                            !dateOnly ||
                                            !slot.start_time ||
                                            !slot.end_time
                                          ) {
                                            return (
                                              <Chip
                                                key={i}
                                                label="Incomplete time slot"
                                                disabled
                                                color="default"
                                                variant="outlined"
                                              />
                                            );
                                          }

                                          const start = parseISO(
                                            `${dateOnly}T${slot.start_time}`,
                                          );
                                          const end = parseISO(
                                            `${dateOnly}T${slot.end_time}`,
                                          );
                                          const label = `${format(
                                            parseISO(dateOnly),
                                            "EEE, MMM d, yyyy",
                                          )} (${format(
                                            start,
                                            "h:mm a",
                                          )} – ${format(end, "h:mm a")})`;

                                          const isSelected =
                                            isAvailabilityChipSelected(
                                              selectedSlot[req.id],
                                              i,
                                            );

                                          return (
                                            <Chip
                                              key={i}
                                              label={label}
                                              clickable
                                              color={
                                                isSelected
                                                  ? "primary"
                                                  : "default"
                                              }
                                              variant={
                                                isSelected
                                                  ? "filled"
                                                  : "outlined"
                                              }
                                              onClick={() =>
                                                setSelectedSlot((prev) => {
                                                  const nextList =
                                                    toggleAvailabilityChip(
                                                      prev[req.id],
                                                      i,
                                                      slot,
                                                    );
                                                  const next = { ...prev };
                                                  if (nextList.length === 0) {
                                                    delete next[req.id];
                                                  } else {
                                                    next[req.id] = nextList;
                                                  }
                                                  return next;
                                                })
                                              }
                                            />
                                          );
                                        })}
                                      </Stack>

                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          display: "block",
                                          mb: 1,
                                          mt: 1,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Appointment start time
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          mb: 4,
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 0.5,
                                            alignItems: "center",
                                          }}
                                        >
                                          <FormControl
                                            size="small"
                                            sx={{
                                              minWidth: 84,
                                              width: 84,
                                              flexShrink: 0,
                                              "& .MuiSelect-select": {
                                                overflow: "visible",
                                                textOverflow: "clip",
                                                paddingRight: "28px !important",
                                              },
                                            }}
                                          >
                                            <InputLabel
                                              id={`legacy-appt-hour-${req.id}`}
                                              shrink
                                            >
                                              Hour
                                            </InputLabel>
                                            <Select
                                              labelId={`legacy-appt-hour-${req.id}`}
                                              label="Hour"
                                              notched
                                              displayEmpty
                                              value={
                                                proposedHours[req.id] ?? ""
                                              }
                                              renderValue={(selected) =>
                                                selected !== "" &&
                                                selected != null
                                                  ? String(selected)
                                                  : "00"
                                              }
                                              onChange={(e) => {
                                                const hour = e.target.value;
                                                setProposedHours((prev) => ({
                                                  ...prev,
                                                  [req.id]: hour,
                                                }));
                                                // Update proposedStartTimes in 24-hour format
                                                if (
                                                  hour &&
                                                  proposedMinutes[req.id] !==
                                                    undefined &&
                                                  proposedAmpm[req.id]
                                                ) {
                                                  const time24 =
                                                    convertTo24Hour(
                                                      hour,
                                                      proposedMinutes[req.id] ||
                                                        "00",
                                                      proposedAmpm[req.id] ||
                                                        "AM",
                                                    );
                                                  setProposedStartTimes(
                                                    (prev) => ({
                                                      ...prev,
                                                      [req.id]: time24,
                                                    }),
                                                  );
                                                }
                                              }}
                                            >
                                              {[
                                                1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
                                                11, 12,
                                              ].map((h) => (
                                                <MenuItem
                                                  key={h}
                                                  value={h.toString()}
                                                >
                                                  {h}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <Typography variant="body2">
                                            :
                                          </Typography>
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 60 }}
                                          >
                                            <InputLabel>Min</InputLabel>
                                            <Select
                                              value={
                                                proposedMinutes[req.id] !==
                                                undefined
                                                  ? proposedMinutes[req.id]
                                                  : ""
                                              }
                                              label="Min"
                                              onChange={(e) => {
                                                const minute = e.target.value;
                                                setProposedMinutes((prev) => ({
                                                  ...prev,
                                                  [req.id]: minute,
                                                }));
                                                // Update proposedStartTimes in 24-hour format
                                                if (
                                                  proposedHours[req.id] &&
                                                  minute !== undefined &&
                                                  proposedAmpm[req.id]
                                                ) {
                                                  const time24 =
                                                    convertTo24Hour(
                                                      proposedHours[req.id] ||
                                                        "9",
                                                      minute,
                                                      proposedAmpm[req.id] ||
                                                        "AM",
                                                    );
                                                  setProposedStartTimes(
                                                    (prev) => ({
                                                      ...prev,
                                                      [req.id]: time24,
                                                    }),
                                                  );
                                                }
                                              }}
                                            >
                                              <MenuItem value="00">00</MenuItem>
                                              <MenuItem value="15">15</MenuItem>
                                              <MenuItem value="30">30</MenuItem>
                                              <MenuItem value="45">45</MenuItem>
                                            </Select>
                                          </FormControl>
                                          <FormControl
                                            size="small"
                                            sx={{ minWidth: 70 }}
                                          >
                                            <InputLabel>AM/PM</InputLabel>
                                            <Select
                                              value={
                                                proposedAmpm[req.id] || "AM"
                                              }
                                              label="AM/PM"
                                              onChange={(e) => {
                                                const ampm = e.target.value;
                                                setProposedAmpm((prev) => ({
                                                  ...prev,
                                                  [req.id]: ampm,
                                                }));
                                                // Update proposedStartTimes in 24-hour format
                                                if (
                                                  proposedHours[req.id] &&
                                                  proposedMinutes[req.id] !==
                                                    undefined &&
                                                  ampm
                                                ) {
                                                  const time24 =
                                                    convertTo24Hour(
                                                      proposedHours[req.id] ||
                                                        "9",
                                                      proposedMinutes[req.id] ||
                                                        "00",
                                                      ampm,
                                                    );
                                                  setProposedStartTimes(
                                                    (prev) => ({
                                                      ...prev,
                                                      [req.id]: time24,
                                                    }),
                                                  );
                                                }
                                              }}
                                            >
                                              <MenuItem value="AM">AM</MenuItem>
                                              <MenuItem value="PM">PM</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Box>

                                        {/* Job Duration Input */}
                                        <TextField
                                          label="Hrs"
                                          size="small"
                                          type="text"
                                          inputMode="decimal"
                                          inputProps={{
                                            style: { textAlign: "center" },
                                          }}
                                          value={displayHrsStored(
                                            jobDurations[req.id],
                                          )}
                                          onChange={(e) => {
                                            let value = filterHrsInput(
                                              e.target.value,
                                            );
                                            if (value === "") value = "0";
                                            setJobDurations((prev) => ({
                                              ...prev,
                                              [req.id]: value,
                                            }));
                                          }}
                                          onBlur={(e) => {
                                            const next = normalizeHrsOnBlur(
                                              e.target.value,
                                            );
                                            setJobDurations((prev) => ({
                                              ...prev,
                                              [req.id]: next,
                                            }));
                                          }}
                                          sx={{
                                            minWidth: "100px",
                                            width: "100px",
                                          }}
                                        />
                                      </Box>
                                    </>
                                  ) : (
                                    <Card
                                      sx={{
                                        p: 2,
                                        bgcolor: "#fff3cd",
                                        border: "1px solid #ffc107",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.primary"
                                        sx={{ mb: 1 }}
                                      >
                                        ⚠️{" "}
                                        <strong>
                                          Customer hasn't submitted available
                                          dates yet.
                                        </strong>
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        You can still submit a bid by proposing
                                        your own dates using the button below.
                                      </Typography>
                                    </Card>
                                  )}

                                  {/* Available Dates */}
                                  <Box>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      sx={{ mt: 4 }}
                                      onClick={() =>
                                        setCounterOffer((prev) => ({
                                          ...prev,
                                          [req.id]: !prev[req.id],
                                        }))
                                      }
                                    >
                                      {counterOffer[req.id]
                                        ? "Hide Alternative Times"
                                        : "Propose Alternative Times"}
                                    </Button>
                                    {counterOffer[req.id] && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ mb: 1 }}
                                        >
                                          <u>
                                            Propose up to 3 alternative times
                                          </u>
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            mb: 1,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Appointment start time
                                        </Typography>

                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            mb: 1,
                                            alignItems: "center",
                                            flexWrap: "nowrap",
                                            overflowX: "auto",
                                            overflowY: "visible",
                                            width: "100%",
                                            pt: 1.25,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              minWidth: 160,
                                              width: 180,
                                              flexShrink: 0,
                                            }}
                                          >
                                            <LocalizationProvider
                                              dateAdapter={AdapterDayjs}
                                            >
                                              <DatePicker
                                                label="Alternative Date"
                                                value={selectedCounterDate}
                                                onChange={(newValue) =>
                                                  setSelectedCounterDate(
                                                    newValue,
                                                  )
                                                }
                                                renderInput={(params) => (
                                                  <TextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                  />
                                                )}
                                              />
                                            </LocalizationProvider>
                                          </Box>

                                          <Box
                                            sx={{
                                              display: "flex",
                                              gap: 0.5,
                                              alignItems: "center",
                                              flexWrap: "nowrap",
                                            }}
                                          >
                                            <FormControl
                                              size="small"
                                              sx={{
                                                minWidth: 84,
                                                width: 84,
                                                flexShrink: 0,
                                                "& .MuiSelect-select": {
                                                  overflow: "visible",
                                                  textOverflow: "clip",
                                                  paddingRight: "28px !important",
                                                },
                                              }}
                                            >
                                              <InputLabel
                                                id={`legacy-alt-hour-${req.id}`}
                                                shrink
                                              >
                                                Hour
                                              </InputLabel>
                                              <Select
                                                labelId={`legacy-alt-hour-${req.id}`}
                                                label="Hour"
                                                notched
                                                displayEmpty
                                                value={altTimeHour ?? ""}
                                                renderValue={(selected) =>
                                                  selected !== "" &&
                                                  selected != null
                                                    ? String(selected)
                                                    : "00"
                                                }
                                                onChange={(e) =>
                                                  setAltTimeHour(e.target.value)
                                                }
                                              >
                                                {[
                                                  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
                                                  11, 12,
                                                ].map((h) => (
                                                  <MenuItem
                                                    key={h}
                                                    value={h.toString()}
                                                  >
                                                    {h}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                            <Typography variant="body2">
                                              :
                                            </Typography>
                                            <FormControl
                                              size="small"
                                              sx={{ minWidth: 60 }}
                                            >
                                              <InputLabel>Min</InputLabel>
                                              <Select
                                                value={altTimeMinute}
                                                label="Min"
                                                onChange={(e) =>
                                                  setAltTimeMinute(
                                                    e.target.value,
                                                  )
                                                }
                                              >
                                                <MenuItem value="00">
                                                  00
                                                </MenuItem>
                                                <MenuItem value="15">
                                                  15
                                                </MenuItem>
                                                <MenuItem value="30">
                                                  30
                                                </MenuItem>
                                                <MenuItem value="45">
                                                  45
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                            <FormControl
                                              size="small"
                                              sx={{ minWidth: 70 }}
                                            >
                                              <InputLabel>AM/PM</InputLabel>
                                              <Select
                                                value={altTimeAmpm || "AM"}
                                                label="AM/PM"
                                                onChange={(e) =>
                                                  setAltTimeAmpm(e.target.value)
                                                }
                                              >
                                                <MenuItem value="AM">
                                                  AM
                                                </MenuItem>
                                                <MenuItem value="PM">
                                                  PM
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Box>

                                          <TextField
                                            label="Hrs"
                                            size="small"
                                            type="text"
                                            inputMode="decimal"
                                            inputProps={{
                                              style: { textAlign: "center" },
                                            }}
                                            value={displayHrsStored(
                                              jobDurations[req.id],
                                            )}
                                            onChange={(e) => {
                                              let value = filterHrsInput(
                                                e.target.value,
                                              );
                                              if (value === "") value = "0";
                                              setJobDurations((prev) => ({
                                                ...prev,
                                                [req.id]: value,
                                              }));
                                            }}
                                            onBlur={(e) => {
                                              const next = normalizeHrsOnBlur(
                                                e.target.value,
                                              );
                                              setJobDurations((prev) => ({
                                                ...prev,
                                                [req.id]: next,
                                              }));
                                            }}
                                            sx={{
                                              minWidth: "100px",
                                              width: "100px",
                                            }}
                                          />

                                          <TextField
                                            label="Est. Finish Time"
                                            size="small"
                                            value={
                                              altTimeHour &&
                                              altTimeMinute &&
                                              altTimeAmpm &&
                                              jobDurations[req.id]
                                                ? (() => {
                                                    try {
                                                      const startTime =
                                                        convertTo24Hour(
                                                          altTimeHour,
                                                          altTimeMinute,
                                                          altTimeAmpm,
                                                        );
                                                      const [h, m] = startTime
                                                        .split(":")
                                                        .map(Number);
                                                      const totalMins =
                                                        h * 60 +
                                                        m +
                                                        parseFloat(
                                                          jobDurations[req.id],
                                                        ) *
                                                          60;
                                                      const endH =
                                                        Math.floor(
                                                          totalMins / 60,
                                                        ) % 24;
                                                      const endM = Math.round(
                                                        totalMins % 60,
                                                      );
                                                      const period =
                                                        endH >= 12
                                                          ? "PM"
                                                          : "AM";
                                                      const displayH =
                                                        endH === 0
                                                          ? 12
                                                          : endH > 12
                                                            ? endH - 12
                                                            : endH;
                                                      return `${displayH}:${String(endM).padStart(2, "0")} ${period}`;
                                                    } catch {
                                                      return "";
                                                    }
                                                  })()
                                                : ""
                                            }
                                            InputProps={{ readOnly: true }}
                                            sx={{
                                              minWidth: "140px",
                                              width: "140px",
                                              flexShrink: 0,
                                            }}
                                          />

                                          <Button
                                            variant="contained"
                                            size="small"
                                            sx={{
                                              whiteSpace: "nowrap",
                                              textTransform: "none",
                                              alignSelf: "center",
                                              backgroundColor:
                                                selectedCounterDate &&
                                                altTimeHour &&
                                                altTimeMinute &&
                                                altTimeAmpm &&
                                                jobDurations[req.id]
                                                  ? "#1976d2"
                                                  : undefined,
                                            }}
                                            onClick={() => {
                                              if (
                                                selectedCounterDate &&
                                                altTimeHour &&
                                                altTimeMinute &&
                                                altTimeAmpm &&
                                                jobDurations[req.id]
                                              ) {
                                                const formattedDate =
                                                  selectedCounterDate.format(
                                                    "YYYY-MM-DD",
                                                  );
                                                const formattedStart =
                                                  convertTo24Hour(
                                                    altTimeHour,
                                                    altTimeMinute,
                                                    altTimeAmpm,
                                                  );
                                                const [h, m] = formattedStart
                                                  .split(":")
                                                  .map(Number);
                                                const totalMins =
                                                  h * 60 +
                                                  m +
                                                  parseFloat(
                                                    jobDurations[req.id],
                                                  ) *
                                                    60;
                                                const endH =
                                                  Math.floor(totalMins / 60) %
                                                  24;
                                                const endM = Math.round(
                                                  totalMins % 60,
                                                );
                                                const formattedEnd = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;

                                                const requestDates =
                                                  counterAvailableDates[
                                                    req.id
                                                  ] || [];

                                                const isDuplicate =
                                                  requestDates.some(
                                                    (d) =>
                                                      d.date ===
                                                        formattedDate &&
                                                      d.start_time ===
                                                        formattedStart &&
                                                      d.end_time ===
                                                        formattedEnd,
                                                  );

                                                if (
                                                  !isDuplicate &&
                                                  (counterAvailableDates[req.id]
                                                    ?.length || 0) <= 3
                                                ) {
                                                  setCounterAvailableDates(
                                                    (prev) => ({
                                                      ...prev,
                                                      [req.id]: [
                                                        ...(prev[req.id] || []),
                                                        {
                                                          date: formattedDate,
                                                          start_time:
                                                            formattedStart,
                                                          end_time:
                                                            formattedEnd,
                                                        },
                                                      ],
                                                    }),
                                                  );
                                                  console.log(
                                                    "Added date",
                                                    counterAvailableDates,
                                                  );
                                                  setSelectedCounterDate(null);
                                                  setAltTimeHour("9");
                                                  setAltTimeMinute("00");
                                                  setAltTimeAmpm("AM");
                                                  setJobDurations((prev) => ({
                                                    ...prev,
                                                    [req.id]: "0",
                                                  }));
                                                }
                                              }
                                            }}
                                            disabled={
                                              !selectedCounterDate ||
                                              !altTimeHour ||
                                              !altTimeMinute ||
                                              !altTimeAmpm ||
                                              !jobDurations[req.id] ||
                                              (counterAvailableDates[req.id]
                                                ?.length || 0) >= 3
                                            }
                                          >
                                            ADD ALTERNATIVE TIME
                                          </Button>
                                        </Box>

                                        <Box mt={1}>
                                          {(counterAvailableDates[req.id] || [])
                                            .filter(
                                              (date) =>
                                                date.date &&
                                                date.start_time &&
                                                date.end_time,
                                            )
                                            .map((date, i) => (
                                              <Chip
                                                key={i}
                                                label={`${format(
                                                  parseISO(date.date),
                                                  "EEEE, MMMM d, yyyy",
                                                )} (${format(
                                                  parseISO(
                                                    `${date.date}T${date.start_time}`,
                                                  ),
                                                  "h:mm a",
                                                )} – ${format(
                                                  parseISO(
                                                    `${date.date}T${date.end_time}`,
                                                  ),
                                                  "h:mm a",
                                                )})`}
                                                onDelete={() =>
                                                  setCounterAvailableDates(
                                                    (prev) => ({
                                                      ...prev,
                                                      [req.id]: prev[
                                                        req.id
                                                      ].filter(
                                                        (_, index) =>
                                                          index !== i,
                                                      ),
                                                    }),
                                                  )
                                                }
                                                sx={{ mr: 1, mt: 1 }}
                                              />
                                            ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                  {/* Extended Service History Button - Only show for Owners */}
                                  {user?.role === "Owner" && (
                                    <Box sx={{ mb: 3 }}>
                                      <Button
                                        onClick={() => {
                                          setSelectedServiceRequest(req);
                                          setServiceHistoryModalOpen(true);
                                        }}
                                        variant={
                                          hasExtendedServiceHistory
                                            ? "outlined"
                                            : "contained"
                                        }
                                        size="small"
                                        fullWidth
                                        sx={{
                                          backgroundColor:
                                            hasExtendedServiceHistory
                                              ? "transparent"
                                              : "#4caf50",
                                          color: hasExtendedServiceHistory
                                            ? "#4caf50"
                                            : "#fff",
                                          borderColor: hasExtendedServiceHistory
                                            ? "#4caf50"
                                            : "transparent",
                                          fontSize: "0.85rem",
                                          padding: "6px 12px",
                                          minHeight: "auto",
                                          whiteSpace: "nowrap",
                                          "&:hover": {
                                            backgroundColor:
                                              hasExtendedServiceHistory
                                                ? "#e8f5e9"
                                                : "#45a049",
                                          },
                                        }}
                                      >
                                        {hasExtendedServiceHistory
                                          ? "📋 Extended History"
                                          : "📋 Buy Extended History"}
                                      </Button>
                                    </Box>
                                  )}

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 3,
                                    }}
                                  >
                                    <TextField
                                      label="Bid Price ($)"
                                      size="small"
                                      type="number"
                                      value={bidAmounts[req.id] || ""}
                                      onChange={(e) =>
                                        handlePriceChange(
                                          req.id,
                                          e.target.value,
                                        )
                                      }
                                      disabled={hasRecentBid}
                                      sx={{ mr: 2, mt: 4 }}
                                    />

                                    <Tooltip
                                      title={
                                        shop.status !== "Active"
                                          ? `Your shop must be Active to submit bids (currently ${shop.status})`
                                          : hasRecentBid
                                            ? "Wait before re-bidding"
                                            : !bidAmounts[req.id]
                                              ? "Enter a bid amount"
                                              : ""
                                      }
                                      arrow
                                    >
                                      <span>
                                        <Button
                                          onClick={() => submitBid(req.id)}
                                          variant="contained"
                                          size="small"
                                          sx={{ mt: 4 }}
                                          disabled={
                                            shop.status !== "Active" ||
                                            hasRecentBid ||
                                            !bidAmounts[req.id]
                                          }
                                        >
                                          {hasRecentBid
                                            ? "Wait to Re-bid"
                                            : "Submit Bid"}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </Box>

                                  {req.bids?.length > 0 && (
                                    <Box
                                      sx={{
                                        border: "1px solid #ccc",
                                        borderRadius: 2,
                                        p: 2,
                                        mb: 2,
                                        backgroundColor: "#e2dadaff",
                                      }}
                                      mt={2}
                                    >
                                      <Typography
                                        sx={{ fontSize: "1.1rem" }}
                                        variant="subtitle2"
                                      >
                                        <strong>Current Bids: </strong>
                                      </Typography>

                                      <List dense>
                                        {req.bids.map((bid, i) => {
                                          // Ensure UTC time is properly converted to local time
                                          // Force UTC parsing by appending 'Z' if not present
                                          const timeString =
                                            bid.bid_time.endsWith("Z")
                                              ? bid.bid_time
                                              : bid.bid_time + "Z";
                                          const bidDate = new Date(timeString);
                                          const bidTimeFormatted =
                                            bidDate.toLocaleString(undefined, {
                                              month: "short",
                                              day: "numeric",
                                              year: "numeric",
                                              hour: "numeric",
                                              minute: "2-digit",
                                              hour12: true,
                                            });

                                          return (
                                            <ListItem
                                              key={i}
                                              alignItems="flex-start"
                                            >
                                              <ListItemText
                                                primary={
                                                  <Box
                                                    display="flex"
                                                    flexDirection={{
                                                      xs: "column",
                                                      sm: "row",
                                                    }}
                                                    alignItems="center"
                                                    gap={1}
                                                    mb={1}
                                                  >
                                                    <Box
                                                      sx={{
                                                        backgroundColor:
                                                          "#e63946",
                                                        color: "#fff",
                                                        borderRadius: "6px",
                                                        px: 1,
                                                        py: 0.25,
                                                        mr: 1,
                                                        fontWeight: "bold",
                                                        fontSize: "1.05rem",
                                                        display: "inline-block",
                                                      }}
                                                    >
                                                      ${bid.offer_price}
                                                    </Box>
                                                    <Typography
                                                      sx={{
                                                        fontSize: "1.00rem",
                                                        fontWeight: 600,
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {bid.shop_name}
                                                    </Typography>
                                                  </Box>
                                                }
                                                secondary={
                                                  <>
                                                    <Typography
                                                      sx={{
                                                        fontSize: "1.05rem",
                                                      }}
                                                    >
                                                      Message:{" "}
                                                      {bid.message || (
                                                        <em>No message</em>
                                                      )}
                                                    </Typography>
                                                    <Typography
                                                      sx={{
                                                        fontSize: "0.95rem",
                                                        color: "gray",
                                                        mt: 0.5,
                                                      }}
                                                    >
                                                      Submitted:{" "}
                                                      {bidTimeFormatted}
                                                    </Typography>

                                                    {bid.selected_available_date && (
                                                      <Typography
                                                        sx={{
                                                          fontSize: "0.95rem",
                                                          color: "gray",
                                                          mt: 0.5,
                                                        }}
                                                      >
                                                        <strong>
                                                          Available Date:
                                                        </strong>{" "}
                                                        {new Date(
                                                          bid.selected_available_date,
                                                        ).toLocaleDateString(
                                                          undefined,
                                                          {
                                                            weekday: "short",
                                                            month: "short",
                                                            day: "numeric",
                                                            year: "numeric",
                                                          },
                                                        )}
                                                      </Typography>
                                                    )}

                                                    {bid.proposed_time &&
                                                      typeof bid.proposed_time ===
                                                        "string" &&
                                                      !bid.proposed_time.includes(
                                                        "{",
                                                      ) && (
                                                        <Typography
                                                          sx={{
                                                            fontSize: "0.95rem",
                                                            color: "gray",
                                                            mt: 0.5,
                                                          }}
                                                        >
                                                          <strong>
                                                            Proposed Start Time:
                                                          </strong>{" "}
                                                          {new Date(
                                                            `1970-01-01T${bid.proposed_time}`,
                                                          ).toLocaleTimeString(
                                                            undefined,
                                                            {
                                                              hour: "numeric",
                                                              minute: "2-digit",
                                                            },
                                                          )}
                                                        </Typography>
                                                      )}

                                                    {bid.estimated_duration && (
                                                      <Typography
                                                        sx={{
                                                          fontSize: "0.95rem",
                                                          color: "gray",
                                                          mt: 0.5,
                                                        }}
                                                      >
                                                        <strong>
                                                          Estimated Duration:
                                                        </strong>{" "}
                                                        {bid.estimated_duration}{" "}
                                                        hour
                                                        {bid.estimated_duration !==
                                                        1
                                                          ? "s"
                                                          : ""}
                                                      </Typography>
                                                    )}

                                                    {bid.proposed_end_time && (
                                                      <Typography
                                                        sx={{
                                                          fontSize: "0.95rem",
                                                          color: "gray",
                                                          mt: 0.5,
                                                        }}
                                                      >
                                                        <strong>
                                                          Estimated End Time:
                                                        </strong>{" "}
                                                        {new Date(
                                                          `1970-01-01T${bid.proposed_end_time}`,
                                                        ).toLocaleTimeString(
                                                          undefined,
                                                          {
                                                            hour: "numeric",
                                                            minute: "2-digit",
                                                          },
                                                        )}
                                                      </Typography>
                                                    )}
                                                  </>
                                                }
                                              />
                                            </ListItem>
                                          );
                                        })}
                                      </List>
                                    </Box>
                                  )}
                                </>
                              }
                              secondaryTypographyProps={{
                                sx: { fontSize: "1.1rem", mb: 2 },
                              }}
                            />
                          </ListItem>
                        </Box>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No service to bid on available.
                  </Typography>
                )}
              </Paper>
            )}

            {/* View Recent Bids - Only show if bidding services are active */}
            {hasBiddingEnabled && (
              <Paper
                elevation={2}
                sx={{
                  mb: 4,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  borderShadow: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#fff",
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#e63926",
                    flexDirection: "row",
                  }}
                  gutterBottom
                >
                  View Recent Bids
                </Typography>
                {pastBids.length > 0 ? (
                  <List sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                    {pastBids.map((bid, idx) => (
                      <ListItem
                        key={idx}
                        divider
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "start", sm: "center" },
                          py: 2,
                        }}
                      >
                        <ListItemText
                          primary={`Bid #${bid.id} - $${bid.price}`}
                          secondary={`Status: ${bid.status} | Request #${bid.requestId} | ${bid.time}`}
                          primaryTypographyProps={{
                            sx: { fontSize: "1.1rem", fontWeight: "bold" },
                          }}
                          secondaryTypographyProps={{
                            sx: { fontSize: "1.1rem" },
                          }}
                        />
                        <Chip
                          label={bid.status}
                          color={
                            bid.status === "Accepted"
                              ? "success"
                              : bid.status === "Rejected"
                                ? "error"
                                : bid.status === "Declined"
                                  ? "default"
                                  : "warning"
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    sx={{ fontSize: "1.1rem" }}
                    variant="body2"
                    color="text.secondary"
                  >
                    No past bids found.
                  </Typography>
                )}
              </Paper>
            )}

            <Paper
              elevation={2}
              sx={{
                mb: 4,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                borderShadow: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: "#fff",
                  mb: 4,
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#e63926",
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#fff",
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  Customers Who Viewed Your Website
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "right" }}>
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: 120,
                      backgroundColor: "#fff",
                      borderRadius: 1,
                      ml: "auto", // push to the right
                    }}
                  >
                    <Select
                      id="filter-select"
                      value={filterType}
                      sx={{ fontSize: "1.1rem" }}
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setFilterValue("");
                      }}
                    >
                      <MenuItem sx={{ fontSize: "1.1rem" }} value="all">
                        All
                      </MenuItem>
                      <MenuItem sx={{ fontSize: "1.1rem" }} value="day">
                        Per Day
                      </MenuItem>
                      <MenuItem sx={{ fontSize: "1.1rem" }} value="week">
                        Per Week
                      </MenuItem>
                      <MenuItem sx={{ fontSize: "1.1rem" }} value="month">
                        Per Month
                      </MenuItem>
                      <MenuItem sx={{ fontSize: "1.1rem" }} value="year">
                        Per Year
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {filterType !== "all" && (
                    <input
                      type={
                        filterType === "day"
                          ? "date"
                          : filterType === "month"
                            ? "month"
                            : filterType === "week"
                              ? "week"
                              : "number"
                      }
                      value={filterValue}
                      placeholder={filterType === "year" ? "e.g. 2024" : ""}
                      min={filterType === "year" ? "2000" : undefined}
                      max={
                        filterType === "year"
                          ? new Date().getFullYear()
                          : undefined
                      }
                      onChange={(e) => setFilterValue(e.target.value)}
                      style={{
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        height: "40px",
                        width: filterType === "year" ? "80px" : "auto",
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Grid container sx={{ flexDirection: "row" }}>
                {visitors.map((user, idx) => (
                  <Grid
                    key={user.id}
                    container
                    sx={{
                      fontWeight: "1.1rem",
                      justifyContent: "center",
                      alignItems: "center",
                      m: 2,
                      cursor: "pointer",
                    }}
                    onClick={() => toggleModal(user)}
                  >
                    <Avatar
                      src={`https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}`}
                      sx={{ width: 64, height: 64, mr: 1 }}
                    />

                    <Typography sx={{ fontSize: "1.1rem" }}>
                      {user.first_name} {user.last_name}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* VISITOR ANALYTICS IN PROGRESS */}
            {/* <Paper
              elevation={2}
              sx={{
                mb: 4,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                borderShadow: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: "#fff",
                  mb: 4,
                  p: 2,
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#e63926",
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#fff",
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  Visitors In The Past 30 Days
                </Typography>
              </Box>
              <LineChart
                xAxis={[{ data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] }]}
                series={[
                  {
                    data: [2, 3, 5.5, 8.5, 1.5, 5, 1, 4, 3, 8],
                    showMark: ({ index }) => index % 2 === 0,
                  },
                ]}
                height={300}
              />
            </Paper> */}
          </Stack>
        </Box>
      </Box>

      {viewUser && selectedVisitor && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <Paper
            elevation={4}
            sx={{
              bgcolor: "#f8fafc",
              borderRadius: 3,
              position: "relative",
              p: 4,
              width: "90%",
              maxWidth: "600px",
            }}
          >
            <IconButton
              onClick={() => toggleModal()}
              sx={{ position: "absolute", top: 12, right: 12 }}
            >
              <Close />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 3,
                fontSize: "1.1rem",
              }}
            >
              <Avatar
                src={`https://ui-avatars.com/api/?name=${selectedVisitor.first_name}+${selectedVisitor.last_name}`}
                sx={{ width: 64, height: 64, mr: 2 }}
              />
              <Box>
                <Typography
                  sx={{ fontSize: "1.1rem" }}
                  variant="h5"
                  fontWeight={600}
                >
                  {selectedVisitor.first_name} {selectedVisitor.last_name}
                </Typography>
                <Typography
                  sx={{ fontSize: "1.1rem" }}
                  variant="body2"
                  color="text.secondary"
                >
                  Recently viewed your shop
                </Typography>
                {selectedVisitor.phone && (
                  <Typography
                    sx={{ fontSize: "0.95rem", mt: 0.5 }}
                    variant="body2"
                    color="primary"
                  >
                    📞 {selectedVisitor.phone}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Visit Date */}
            {selectedVisitor.visit_date && (
              <Card sx={{ mb: 3, p: 2, bgcolor: "#e8f5e9" }}>
                <Typography
                  variant="body2"
                  textAlign="center"
                  color="text.secondary"
                >
                  <strong>Last Visit:</strong>{" "}
                  {new Date(selectedVisitor.visit_date).toLocaleString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    },
                  )}
                </Typography>
              </Card>
            )}

            {selectedVisitor.cars?.length > 0 && (
              <Card sx={{ mb: 3, p: 2 }}>
                <Typography
                  sx={{ fontSize: "1.1rem" }}
                  variant="h6"
                  gutterBottom
                  textAlign="center"
                >
                  <strong>Vehicles</strong>
                </Typography>
                <List dense>
                  {selectedVisitor.cars.map((car, i) => (
                    <ListItem key={`${car}_${i}`} divider>
                      <ListItemText primary={car} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}

            {selectedVisitor.obdCodes?.length > 0 && (
              <Card sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom textAlign="center">
                  Recent OBD2 Searches
                </Typography>
                <List dense>
                  {selectedVisitor.obdCodes.map((entry, i) => (
                    <ListItem key={`${entry.code}_${i}`} divider>
                      <ListItemText
                        primary={`Code: ${String(entry.code)}`}
                        secondary={`Car: ${String(entry.car)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Paper>
        </Box>
      )}
      {showCompletionModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 1200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Paper
            sx={{
              p: 4,
              borderRadius: 2,
              maxWidth: "90%",
              width: 600,
              maxHeight: "90vh", // 👈 limit height
              overflowY: "auto", // 👈 allow vertical scroll
            }}
          >
            <Typography variant="h6" gutterBottom>
              Complete Service
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Mechanics</InputLabel>
              <Select
                multiple
                value={selectedMechanics}
                onChange={(e) => {
                  setSelectedMechanics(e.target.value);
                }}
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const m = mechanics.find((m) => m.id === id);
                      return m?.name;
                    })
                    .join(", ")
                }
              >
                {mechanics.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Services Completed</InputLabel>
              <Select
                multiple
                value={selectedServices}
                onChange={(e) => setSelectedServices(e.target.value)}
                label="Services Completed"
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const s = availableServices.find((s) => s.id === id);
                      return s?.service_name;
                    })
                    .join(", ")
                }
              >
                {availableServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    <Checkbox checked={selectedServices.includes(service.id)} />
                    <ListItemText primary={service.service_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedServices.map((serviceId) => {
              const service = availableServices.find((s) => s.id === serviceId);
              const costs = serviceCosts[serviceId] || { parts: "", labor: "" };

              return (
                <Grid
                  container
                  key={serviceId}
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  {/* Service Name on Left */}
                  <Grid item xs={12} sm={4}>
                    <Typography fontWeight="bold">
                      {service?.service_name}
                    </Typography>
                  </Grid>

                  {/* Input Fields Aligned Right */}
                  <Grid item xs={12} sm={8}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      <TextField
                        label="Parts Cost ($)"
                        type="number"
                        value={costs.parts}
                        onChange={(e) =>
                          setServiceCosts((prev) => ({
                            ...prev,
                            [serviceId]: {
                              ...prev[serviceId],
                              parts: e.target.value,
                            },
                          }))
                        }
                      />
                      <TextField
                        label="Labor Cost ($)"
                        type="number"
                        value={costs.labor}
                        onChange={(e) =>
                          setServiceCosts((prev) => ({
                            ...prev,
                            [serviceId]: {
                              ...prev[serviceId],
                              labor: e.target.value,
                            },
                          }))
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}

            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              sx={{ mb: 2 }}
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
            />

            <TextField
              label="Updated Car Mileage"
              type="number"
              fullWidth
              sx={{ mb: 2 }}
              value={newMileage}
              onChange={(e) => setNewMileage(e.target.value)}
            />

            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "rgba(0, 0, 0, 0.6)" }}
                >
                  OBD2 Codes (Optional)
                </Typography>
                <Tooltip
                  title="OBD2 (On-Board Diagnostics) codes are diagnostic trouble codes that identify specific issues with your vehicle's engine, transmission, or other systems. Examples: P0420 (Catalytic Converter), P0171 (System Too Lean). Enter any codes found during service."
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ padding: 0.5 }}>
                    <Info fontSize="small" sx={{ color: "#1976d2" }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Autocomplete
                multiple
                freeSolo
                options={obdCodes} // [{ code, description }]
                value={selectedObdCode} // [{ code, description }]
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : `${option.code} - ${option.description || "No description"}`
                }
                onChange={(e, newValue) =>
                  setSelectedObdCode(newValue.filter(Boolean))
                }
                inputValue={typedCode}
                onInputChange={(e, val) => setTypedCode(val)}
                renderInput={(params) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: "15px",
                          },
                        },
                      }}
                      title="If OBD2 code is not in dropdown, please type it in manually and click +"
                    >
                      <TextField
                        {...params}
                        placeholder="Type code (e.g., P0420) or select from dropdown"
                        fullWidth
                      />
                    </Tooltip>
                    <Button variant="contained" onClick={handleAddCustomCode}>
                      +
                    </Button>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
              />
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setShowCompletionModal(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={submitCompletedService}>
                Submit
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
      {completedModalOpen && selectedCompleted && (
        <Modal
          open={completedModalOpen}
          onClose={() => setCompletedModalOpen(false)}
        >
          <Paper
            sx={{
              p: 4,
              m: "auto",
              mt: 10,
              maxWidth: 600,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 2,
                p: 2,
                mb: 2,
                backgroundColor: "#e63926",
              }}
            >
              <Typography
                sx={{ color: "#fff", fontSize: "1.1rem", mr: 3 }}
                variant="h6"
                gutterBottom
              >
                <strong>Completed Service Details </strong>{" "}
                <Chip
                  label={selectedCompleted.request.status}
                  size="small"
                  sx={{ ml: 18 }}
                  color={
                    selectedCompleted.request.status === "Completed"
                      ? "success"
                      : selectedCompleted.request.status === "Cancelled"
                        ? "error"
                        : "warning"
                  }
                />
              </Typography>
            </Box>
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Car Owner:</strong> {selectedCompleted.request.user_name}
            </Typography>
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Email:</strong> {selectedCompleted.request.email}
            </Typography>
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Phone:</strong> {selectedCompleted.request.phone_number}
            </Typography>
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Car:</strong>{" "}
              {`${selectedCompleted.request.year} ${selectedCompleted.request.make} ${selectedCompleted.request.model} (${selectedCompleted.request.mileage} mi)`}
            </Typography>
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Date:</strong>{" "}
              {new Date(
                selectedCompleted.request.activity_date,
              ).toLocaleDateString()}
            </Typography>
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Final Price:</strong>{" "}
              {selectedCompleted?.request?.final_price > 0
                ? `$${selectedCompleted.request.final_price}`
                : "0"}
            </Typography>

            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Services:</strong>
            </Typography>

            {selectedCompleted?.services?.length > 0 ? (
              <ul>
                {selectedCompleted.services.map((s) => (
                  <li key={s.service_name}>
                    {s.service_name} - Parts: ${s.final_price_parts} | Labor: $
                    {s.final_price_labor}
                  </li>
                ))}
              </ul>
            ) : (
              <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
                No Services Completed
              </Typography>
            )}

            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Mechanics:</strong>
            </Typography>

            {selectedCompleted?.mechanics?.length > 0 ? (
              <ul>
                {selectedCompleted.mechanics.map((m, idx) => {
                  // Find mechanic rating for this specific mechanic (customer rates mechanic)
                  // The mechanic rating is per service request, so we show it for all mechanics on that request
                  const mechanicRating =
                    platformRatings.mechanic_rating || null;

                  return (
                    <li
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {m.first_name} {m.last_name}
                      </span>
                      {mechanicRating ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            "&:hover": { opacity: 0.7 },
                          }}
                          onClick={() => {
                            setSelectedMechanicForRating(mechanicRating);
                            setCustomerRatingModalOpen(true);
                          }}
                        >
                          <Rating
                            value={mechanicRating.rating || 0}
                            size="small"
                            readOnly
                          />
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          (not rated)
                        </Typography>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
                No mechanics worked on this
              </Typography>
            )}
            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>Notes:</strong>
            </Typography>

            {selectedCompleted?.request?.notes ? (
              <ul>
                <li>{selectedCompleted.request.notes}</li>
              </ul>
            ) : (
              <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
                No notes provided
              </Typography>
            )}

            <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
              <strong>OBD2 Code:</strong> scanned on service date
            </Typography>

            {selectedCompleted?.scannedObdCodes?.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                <ul>
                  {selectedCompleted.scannedObdCodes.map((code, index) => (
                    <li key={index}>
                      {code.code}
                      {code.description ? ` - ${code.description}` : ""}
                    </li>
                  ))}
                </ul>
              </Box>
            ) : (
              <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
                No OBD2 codes scanned on service date.
              </Typography>
            )}

            {/* Rating Sections at Bottom */}
            <Box mt={3}>
              {/* Customer Rating - Mechanic rates customer (editable) */}
              <Box
                p={2}
                mb={2}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  bgcolor: "#fff",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Rate the Customer
                </Typography>
                <Rating
                  value={platformRatings.customer_rating?.rating || 0}
                  size="large"
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      handleOpenRatingModal("customer");
                    }
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenRatingModal("customer");
                  }}
                  sx={{ cursor: "pointer" }}
                />
                {platformRatings.customer_rating?.comment && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: "#333" }}
                    >
                      "{platformRatings.customer_rating.comment}"
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Rate Autoshops.com - One per user, editable */}
              <Box
                p={2}
                mb={2}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  bgcolor: "#fff",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Rate Autoshops.com
                </Typography>
                <Rating
                  value={platformRatings.platform_rating?.rating || 0}
                  size="large"
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      handleOpenRatingModal("platform");
                    }
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenRatingModal("platform");
                  }}
                  sx={{ cursor: "pointer" }}
                />
                {platformRatings.platform_rating?.comment && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: "#333" }}
                    >
                      "{platformRatings.platform_rating.comment}"
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Google Review - Display star rating from Service_Reviews table (customer's review) */}
              <Box
                p={2}
                mb={2}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Service Rating (Google Review)
                </Typography>
                {selectedCompleted?.request?.service_rating ? (
                  <>
                    <Rating
                      value={selectedCompleted.request.service_rating || 0}
                      size="large"
                      readOnly
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedRatingType(null); // Google review - view only
                        setRatingModalOpen(true);
                      }}
                      sx={{ cursor: "pointer" }}
                    />
                    {selectedCompleted.request.service_comment && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontStyle: "italic", color: "#666" }}
                      >
                        "{selectedCompleted.request.service_comment}"
                      </Typography>
                    )}
                    <Box
                      sx={{
                        mt: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1,
                        py: 0.5,
                        bgcolor: "rgba(66, 133, 244, 0.1)",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#4285F4", fontWeight: 500 }}
                      >
                        Google Review
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    No Google rating yet
                  </Typography>
                )}
              </Box>
            </Box>

            <Box textAlign="right" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCompletedModalOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Paper>
        </Modal>
      )}

      {/* Customer Rating Detail Modal (Mechanic Rating from Customer) */}
      <Dialog
        open={customerRatingModalOpen}
        onClose={() => setCustomerRatingModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Customer Rating</DialogTitle>
        <DialogContent>
          {selectedMechanicForRating && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Rating from Customer
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Rating
                  value={selectedMechanicForRating.rating || 0}
                  size="large"
                  readOnly
                />
                <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
                  {selectedMechanicForRating.rating || 0} out of 5 stars
                </Typography>
              </Box>
              {selectedMechanicForRating.comment && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Comment
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      bgcolor: "#f5f5f5",
                      borderRadius: 2,
                      fontStyle: "italic",
                      color: "#333",
                    }}
                  >
                    "{selectedMechanicForRating.comment}"
                  </Typography>
                </Box>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                This rating was provided by the customer and cannot be edited.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerRatingModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Modal */}
      <Dialog
        open={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedRatingType === "customer" && "Rate the Customer"}
          {selectedRatingType === "platform" && "Rate Autoshops.com"}
          {!selectedRatingType && "Service Ratings"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Rate the Customer - Editable */}
            {selectedRatingType === "customer" && (
              <Box
                sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: 2 }}
              >
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Rate the Customer
                </Typography>
                <Rating
                  value={ratingModalRating}
                  size="large"
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      setRatingModalRating(newValue);
                    }
                  }}
                  sx={{ cursor: "pointer" }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Comment (optional)"
                  value={ratingModalComment}
                  onChange={(e) => setRatingModalComment(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="Share your experience with this customer..."
                />
              </Box>
            )}

            {/* Rate Autoshops.com - Editable */}
            {selectedRatingType === "platform" && (
              <Box
                sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: 2 }}
              >
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Rate Autoshops.com
                </Typography>
                <Rating
                  value={ratingModalRating}
                  size="large"
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      setRatingModalRating(newValue);
                    }
                  }}
                  sx={{ cursor: "pointer" }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Comment (optional)"
                  value={ratingModalComment}
                  onChange={(e) => setRatingModalComment(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="Share your experience with Autoshops.com..."
                />
              </Box>
            )}

            {/* Google Review - Display only with link from Service_Reviews table */}
            {!selectedRatingType &&
              selectedCompleted?.request?.service_rating && (
                <Box sx={{ p: 2, bgcolor: "#fafafa", borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Service Rating (Google Review)
                  </Typography>
                  <Rating
                    value={selectedCompleted.request.service_rating || 0}
                    size="large"
                    readOnly
                  />
                  {selectedCompleted.request.service_comment && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontStyle: "italic", color: "#666" }}
                    >
                      "{selectedCompleted.request.service_comment}"
                    </Typography>
                  )}
                  <Box
                    sx={{
                      mt: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                      bgcolor: "rgba(66, 133, 244, 0.1)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "#4285F4", fontWeight: 500 }}
                    >
                      Google Review
                    </Typography>
                  </Box>
                  {selectedCompleted.request.place_id && (
                    <Button
                      variant="outlined"
                      href={`https://search.google.com/local/reviews?placeid=${selectedCompleted.request.place_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mt: 2,
                        color: "#4285F4",
                        borderColor: "#4285F4",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#1a73e8",
                          bgcolor: "rgba(66, 133, 244, 0.04)",
                        },
                      }}
                    >
                      View All Google Reviews →
                    </Button>
                  )}
                </Box>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingModalOpen(false)}>Cancel</Button>
          {selectedRatingType && (
            <Button
              onClick={handleSaveRating}
              variant="contained"
              disabled={savingRating || ratingModalRating < 1}
            >
              {savingRating ? "Saving..." : "Save Rating"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelQuoteDialogOpen}
        onClose={() => setCancelQuoteDialogOpen(false)}
      >
        <DialogTitle>Cancel Quote</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to cancel your quote for this service request?
          </Typography>
          <TextField
            label="Reason for cancellation"
            fullWidth
            multiline
            rows={3}
            value={cancelQuoteReason}
            onChange={(e) => setCancelQuoteReason(e.target.value)}
            placeholder="Enter a brief explanation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelQuoteDialogOpen(false)}>
            Keep Quote
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              const success = await cancelQuote(
                selectedRequestId,
                cancelQuoteReason,
              );
              if (success) {
                setCancelQuoteDialogOpen(false);
                setSelectedRequestId(null);
                setCancelQuoteReason("");
                await fetchDashboardData(); // Reload dashboard so cancelled quote state is reflected
              } else {
                alert("Could not cancel quote.");
              }
            }}
          >
            Yes, Cancel Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancel Service</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to cancel this service? Please include a
            reason.
          </Typography>
          <TextField
            label="Reason for cancellation"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Explain why the service is being cancelled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              const success = await cancelServiceRequest(
                cancelRequestId,
                cancelReason,
              );
              if (success) {
                setInProgressServices((prev) =>
                  prev.filter((r) => r.id !== cancelRequestId),
                );
                setCancelDialogOpen(false);
                setCancelReason("");
                setCancelRequestId(null);
              } else {
                alert("Could not cancel the service.");
              }
            }}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Modal - Shows on first login when status != Active */}
      <Dialog
        open={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          localStorage.setItem(`statusModal_${shop.status}_seen`, "true");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor:
              shop.status === "Pending"
                ? "#fbbf24"
                : shop.status === "Suspended"
                  ? "#dc2626"
                  : shop.status === "Inactive"
                    ? "#17a2b8"
                    : "#6b7280",
            color: "white",
            fontWeight: 600,
            fontSize: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {shop.status === "Pending" && "⏳ Shop Pending Approval"}
          {shop.status === "Suspended" && "⚠️ Shop Suspended"}
          {shop.status === "Inactive" && "ℹ️ Shop Inactive"}
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {shop.status === "Pending" && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Welcome to Autoshops.com!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your shop application is currently under review by our admin
                team. Some features are limited until your shop is approved.
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#fef3c7",
                  borderRadius: 1,
                  border: "1px solid #fbbf24",
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#92400e", mb: 1 }}
                >
                  📋 Approval Checklist:
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" sx={{ color: "#92400e" }}>
                    {shop.confidence_score >= 50 ? "✅" : "⏳"} Confidence
                    Score: {shop.confidence_score}%{" "}
                    {shop.confidence_score < 50 && "(Need 50%+)"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#92400e" }}>
                    {shop.business_license_url ? "✅" : "⏳"} Business License{" "}
                    {!shop.business_license_url && "(Upload in profile)"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#92400e" }}>
                    {shop.storefront_photo_url ? "✅" : "⏳"} Storefront Photo{" "}
                    {!shop.storefront_photo_url && "(Upload in profile)"}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                You'll be notified once your shop is activated. In the meantime,
                you can complete your profile and familiarize yourself with the
                dashboard.
              </Typography>
            </>
          )}

          {shop.status === "Suspended" && (
            <>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: "#dc2626" }}
              >
                Your shop has been suspended
              </Typography>
              {shop.rejection_reason && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#fee2e2",
                    borderRadius: 1,
                    border: "1px solid #dc2626",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#7f1d1d", mb: 1 }}
                  >
                    Reason for suspension:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#7f1d1d", whiteSpace: "pre-line" }}
                  >
                    {shop.rejection_reason}
                  </Typography>
                </Box>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your shop is currently suspended and not visible to customers.
                All features are disabled until the issues are resolved.
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Please address the issues mentioned above and contact our
                support team to request reactivation.
              </Typography>
            </>
          )}

          {shop.status === "Inactive" && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Your shop is currently inactive
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your shop is not visible to customers and some features are
                limited. You can request reactivation at any time.
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Click "Request Review" in the banner above to submit a
                reactivation request to our admin team.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setStatusModalOpen(false);
              localStorage.setItem(`statusModal_${shop.status}_seen`, "true");
            }}
            variant="contained"
            sx={{
              backgroundColor:
                shop.status === "Pending"
                  ? "#fbbf24"
                  : shop.status === "Suspended"
                    ? "#dc2626"
                    : shop.status === "Inactive"
                      ? "#17a2b8"
                      : "#6b7280",
              "&:hover": {
                backgroundColor:
                  shop.status === "Pending"
                    ? "#f59e0b"
                    : shop.status === "Suspended"
                      ? "#991b1b"
                      : shop.status === "Inactive"
                        ? "#138496"
                        : "#4b5563",
              },
            }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Conversation Dialog */}
      <Dialog
        open={reviewConversationOpen}
        onClose={() => setReviewConversationOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review Conversation
          {selectedReview && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedReview.first_name} {selectedReview.last_name} •{" "}
              {selectedReview.rating} ⭐
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {/* Conversation Thread */}
          {loadingComments ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : reviewComments.length > 0 ? (
            <Stack spacing={2} mb={3}>
              {reviewComments.map((c) => (
                <Paper
                  key={c.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: c.author_type === "shop" ? "#fff3e0" : "#f5f5f5",
                    borderLeft:
                      c.author_type === "shop"
                        ? "4px solid #ff9800"
                        : "4px solid #2196f3",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {c.author_type === "shop"
                        ? c.shop_name || "Shop"
                        : c.author_name}
                      {c.author_type === "household" && " (Household Member)"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {c.comment}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              py={3}
            >
              No comments yet.
            </Typography>
          )}

          {/* Reply Form (Owners Only) */}
          {user?.role === "Owner" && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Your Reply
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Write your reply to the customer..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={submitShopReply}
                disabled={submittingReply || !replyText.trim()}
              >
                {submittingReply ? "Posting..." : "Post Reply"}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewConversationOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extended Service History Modal */}
      <Dialog
        open={serviceHistoryModalOpen}
        onClose={() => setServiceHistoryModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {hasExtendedServiceHistory
            ? "Extended Service History"
            : user?.role === "Owner"
              ? "Upgrade to Extended Service History"
              : "Service History"}
        </DialogTitle>
        <DialogContent>
          {!hasExtendedServiceHistory && user?.role === "Owner" ? (
            // Upgrade Prompt - Only show for Owners
            <Box sx={{ py: 3 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  🔓 Unlock Complete Customer Service History
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Get access to complete service history for all your incoming
                  customers across ALL autoshops. This powerful tool helps you:
                </Typography>
                <ul style={{ marginTop: "10px", marginBottom: "15px" }}>
                  <li>
                    View customer's complete maintenance records from other
                    shops
                  </li>
                  <li>Make better-informed service recommendations</li>
                  <li>Identify recurring issues and patterns</li>
                  <li>Provide more accurate estimates and diagnostics</li>
                  <li>Build trust with transparent, data-driven advice</li>
                </ul>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#4caf50", mb: 2 }}
                >
                  Only $19.99/month
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic", mb: 3 }}
                >
                  Cancel anytime. Access history for all incoming service
                  requests once they're assigned to your shop.
                </Typography>
              </Alert>
              <Box display="flex" justifyContent="center" gap={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setServiceHistoryModalOpen(false);
                    navigate("/pricing");
                  }}
                  sx={{
                    backgroundColor: "#4caf50",
                    "&:hover": { backgroundColor: "#45a049" },
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Upgrade Now - $19.99/mo
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setServiceHistoryModalOpen(false)}
                >
                  Maybe Later
                </Button>
              </Box>
            </Box>
          ) : !hasExtendedServiceHistory && user?.role === "Mechanic" ? (
            // Mechanics without extended history - show message that only owners can upgrade
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Alert severity="info">
                <Typography variant="body1">
                  Extended Service History is only available to shop owners.
                  Please contact your shop owner to upgrade.
                </Typography>
              </Alert>
            </Box>
          ) : (
            // Service History View (Printable)
            <Box id="printable-service-history" sx={{ p: 3 }}>
              {/* Top Center - Shop Info */}
              <Box
                sx={{
                  textAlign: "center",
                  mb: 4,
                  borderBottom: "2px solid #e63926",
                  pb: 2,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "#e63926", mb: 1 }}
                >
                  {shop?.name || "Auto Shop"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {shop?.address?.street && `${shop.address.street}, `}
                  {shop?.address?.city && `${shop.address.city}, `}
                  {shop?.address?.state} {shop?.address?.zip}
                </Typography>
                <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
                  Customer Service History Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generated on{" "}
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </Box>

              {/* Customer & Car Info Row */}
              <Box sx={{ display: "flex", gap: 4, mb: 4 }}>
                {/* Left - Customer Info */}
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, color: "#e63926" }}
                  >
                    Customer Information
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Name:</strong>{" "}
                    {selectedServiceRequest?.user || "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Request ID:</strong> #
                    {selectedServiceRequest?.id || "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Requested On:</strong>{" "}
                    {selectedServiceRequest?.requestedOn
                      ? new Date(
                          selectedServiceRequest.requestedOn,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </Typography>
                </Box>

                {/* Right - Car Info */}
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, color: "#e63926" }}
                  >
                    Vehicle Information
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Vehicle:</strong>{" "}
                    {selectedServiceRequest?.carDetails?.year}{" "}
                    {selectedServiceRequest?.carDetails?.make}{" "}
                    {selectedServiceRequest?.carDetails?.model}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>VIN:</strong>{" "}
                    {selectedServiceRequest?.carDetails?.vin || "N/A"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Mileage:</strong>{" "}
                    {selectedServiceRequest?.carDetails?.mileage
                      ? `${selectedServiceRequest.carDetails.mileage.toLocaleString()} mi`
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>

              {/* Service History Table */}
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", mb: 2, color: "#e63926" }}
              >
                Complete Service History
              </Typography>
              {selectedServiceRequest?.customerServiceHistory &&
              selectedServiceRequest.customerServiceHistory.length > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      border: "1px solid #ddd",
                    }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#e63926", color: "white" }}
                      >
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          Service
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          Shop
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedServiceRequest.customerServiceHistory.map(
                        (record, idx) => (
                          <tr
                            key={idx}
                            style={{
                              backgroundColor:
                                idx % 2 === 0 ? "#f9f9f9" : "white",
                            }}
                          >
                            <td
                              style={{
                                padding: "10px",
                                borderBottom: "1px solid #ddd",
                              }}
                            >
                              {new Date(record.date).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </td>
                            <td
                              style={{
                                padding: "10px",
                                borderBottom: "1px solid #ddd",
                              }}
                            >
                              {record.service}
                            </td>
                            <td
                              style={{
                                padding: "10px",
                                borderBottom: "1px solid #ddd",
                              }}
                            >
                              {record.shop}
                            </td>
                            <td
                              style={{
                                padding: "10px",
                                borderBottom: "1px solid #ddd",
                              }}
                            >
                              {record.description || "N/A"}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No service history available for this vehicle.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {hasExtendedServiceHistory && (
            <Button
              variant="contained"
              onClick={() => {
                const printContent = document.getElementById(
                  "printable-service-history",
                );
                const printWindow = window.open(
                  "",
                  "",
                  "height=800,width=1000",
                );
                printWindow.document.write(
                  "<html><head><title>Service History Report</title>",
                );
                printWindow.document.write("<style>");
                printWindow.document.write(
                  "body { font-family: Arial, sans-serif; margin: 20px; }",
                );
                printWindow.document.write(
                  "table { width: 100%; border-collapse: collapse; margin-top: 20px; }",
                );
                printWindow.document.write(
                  "th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }",
                );
                printWindow.document.write(
                  "th { background-color: #e63926; color: white; }",
                );
                printWindow.document.write(
                  "tr:nth-child(even) { background-color: #f9f9f9; }",
                );
                printWindow.document.write("</style>");
                printWindow.document.write("</head><body>");
                printWindow.document.write(printContent.innerHTML);
                printWindow.document.write("</body></html>");
                printWindow.document.close();
                printWindow.print();
              }}
              sx={{
                backgroundColor: "#e63926",
                "&:hover": { backgroundColor: "#d32f2f" },
              }}
            >
              🖨️ Print Report
            </Button>
          )}
          <Button onClick={() => setServiceHistoryModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversation Modal */}
      <ConversationModal
        open={conversationModalOpen}
        onClose={() => setConversationModalOpen(false)}
        shopName={shop?.name}
        serviceRequestId={selectedConversationRequest?.id}
        comments={conversationComments}
        loading={loadingConversation}
        onSendMessage={handleSendConversationMessage}
        currentUserType="shop"
      />

      <Dialog
        open={messageCenterOpen}
        onClose={() => setMessageCenterOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 3,
          }}
        >
          Shop Messages
          {unreadMessageCount > 0 && (
            <Chip
              label={`${unreadMessageCount} unread`}
              color="error"
              size="small"
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingMessagesSummary ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, textTransform: "uppercase", mb: 1 }}
                >
                  Active Conversations
                </Typography>
                {activeConversations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No unread messages.
                  </Typography>
                ) : (
                  <List dense>
                    {activeConversations.map((convo) => (
                      <ListItemButton
                        key={`active-${convo.service_request_id}`}
                        alignItems="flex-start"
                        onClick={() => handleOpenConversationFromSummary(convo)}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Typography sx={{ fontWeight: 600 }}>
                                {`Request #${convo.service_request_id} • ${convo.customer_name || "Customer"}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {convo.last_message?.created_at
                                  ? formatDistanceToNow(
                                      new Date(convo.last_message.created_at),
                                      { addSuffix: true },
                                    )
                                  : "Just now"}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <>
                              {convo.vehicle && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "text.secondary",
                                  }}
                                >
                                  {convo.vehicle}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {convo.last_message
                                  ? `${convo.last_message.author_type === "customer" ? "Customer" : "Shop"}: ${getMessageSnippet(convo.last_message.snippet)}`
                                  : "No messages yet"}
                              </Typography>
                            </>
                          }
                        />
                        {convo.unread_count > 0 && (
                          <Chip
                            label={`${convo.unread_count} new`}
                            color="error"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, textTransform: "uppercase", mb: 1 }}
                >
                  Recent History
                </Typography>
                {historyConversations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No previous conversations yet.
                  </Typography>
                ) : (
                  <List dense>
                    {historyConversations.map((convo) => (
                      <ListItemButton
                        key={`history-${convo.service_request_id}`}
                        alignItems="flex-start"
                        onClick={() => handleOpenConversationFromSummary(convo)}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Typography sx={{ fontWeight: 600 }}>
                                {`Request #${convo.service_request_id} • ${convo.customer_name || "Customer"}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {convo.last_message?.created_at
                                  ? formatDistanceToNow(
                                      new Date(convo.last_message.created_at),
                                      { addSuffix: true },
                                    )
                                  : "—"}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <>
                              {convo.vehicle && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "text.secondary",
                                  }}
                                >
                                  {convo.vehicle}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {convo.last_message
                                  ? `${convo.last_message.author_type === "customer" ? "Customer" : "Shop"}: ${getMessageSnippet(convo.last_message.snippet)}`
                                  : "No messages yet"}
                              </Typography>
                            </>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => fetchMessageSummary({ showLoader: true })}>
            Refresh
          </Button>
          <Button onClick={() => setMessageCenterOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Extended Service History Purchase Modal - Only accessible to Owners */}
      {user?.role === "Owner" && (
        <Dialog
          open={extendedHistoryModalOpen}
          onClose={() => setExtendedHistoryModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            ⚡ Unlock Extended Service History
          </DialogTitle>
          <DialogContent dividers>
            {extendedHistoryPrice !== null && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#1976d2" }}
                >
                  ${extendedHistoryPrice.toFixed(2)}/month
                </Typography>
              </Box>
            )}
            <Typography sx={{ mb: 2 }}>
              Give your team the full picture before quoting or scheduling a
              job. Extended Service History helps you:
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem disableGutters>
                <ListItemText primary="See completed work from other Autoshops.com partners on the same vehicle." />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Use advanced filters (Last 30/90 days, This Month/Year, All Time)." />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Spot repeat issues faster and win trust with informed recommendations." />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              This add-on is billed monthly. Cancel anytime directly from your
              subscription dashboard.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setExtendedHistoryModalOpen(false)}>
              Maybe Later
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#e63926",
                "&:hover": { backgroundColor: "#c6281d" },
              }}
              onClick={() => {
                setExtendedHistoryModalOpen(false);
                navigate("/pricing#extended-history");
              }}
            >
              Subscribe Now
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <BillingRequiredModal
        open={billingModalOpen}
        onClose={() => {
          sessionStorage.setItem("billing_setup_dismissed", "1");
          setPendingBillingRequestId(null);
          setBillingModalOpen(false);
        }}
        isOwner={user?.role === "Owner"}
        mechanicId={user?.id}
        shopId={shop?.id}
        returnPath={buildDashboardBillingReturnPath(shop?.id, pendingBillingRequestId)}
        gateMessage={billingGateMessage}
        onSetupComplete={() => {
          setBillingReadyForQuotes(true);
          setBillingGateMessage(null);
          setBillingModalOpen(false);
          applyQuoteResumeAfterBilling();
        }}
      />

      <PlatformFeeRateChangeModal
        open={rateChangeModalOpen}
        onClose={() => {
          setRateChangeModalOpen(false);
          setPendingBidRequestId(null);
          setPendingConfirmRequestId(null);
        }}
        notice={rateNotice}
        shopId={shop?.id}
        isOwner={user?.role === "Owner"}
        onAcknowledged={async () => {
          const bidId = pendingBidRequestId;
          const confirmId = pendingConfirmRequestId;
          setPendingBidRequestId(null);
          setPendingConfirmRequestId(null);
          setRateNotice(null);
          if (bidId) {
            await submitBid(bidId);
          } else if (confirmId) {
            await confirmShopAppointment(confirmId);
          }
        }}
      />

      <Footer />
    </>
  );
};

export default MechanicDashboard;
